import React, { useEffect, useMemo, useState, useRef } from 'react';
import { BILLING_BRAND } from '../data/billingBrandConfig';

// Nombre de marca por app (Impulza / Gastify). billingBrandConfig esta
// PROTECTED en el sync, asi que cada app conserva su brandName sin
// hardcodear "Impulza" aca.
const BRAND_NAME = BILLING_BRAND?.brandName || 'el sistema';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';
// import jsPDF from 'jspdf'; // Comentado legacy
// import autoTable from 'jspdf-autotable'; // Comentado legacy
import QRCode from 'qrcode'; // ✅ IMPORTANTE: Librería para generar el QR real
import toast from 'react-hot-toast';
import {
  getEntityMonthLimit as getEntityMonthLimitUtil,
  getEntityRemaining as getEntityRemainingUtil,
  hasMonthLimitConfigured,
  validateBillingEntityForInvoice,
} from '../utils/billingEntityLimits';
import {
  FileText,
  CloudUpload,
  RefreshCcw,
  Search,
  Filter,
  MapPin,
  AlertCircle,
  Calendar,
  DollarSign,
  Printer,
  Undo2,
  AlertTriangle,
  X,
  ArrowDown,
  Layers,
  Settings,
  CreditCard, 
  ChevronDown,
  ChevronUp,
  Check,
  FilePlus2
} from 'lucide-react';
import { getSaleItemDisplayName } from '../utils/productLabels';
import { buildAfipQrUrl, buildFiscalTicketHtml, resolveTicketEmitter } from '../utils/ticketFiscal';
import { persistSelectedSiteId, resolvePreferredSiteId } from '../utils/sitePreferences';
import { buildAfipIvaBreakdown, selectBillableItems, getItemIvaPercent, afipPercentFromIvaId } from '../utils/afipIva';
import VentaAdministrativaModal from '../components/billing/VentaAdministrativaModal';
import { canCreateVentaAdministrativa, VENTA_ADMINISTRATIVA_ORIGEN } from '../utils/ventaAdministrativa';
import { fetchEffectiveModulePermissions } from '../utils/userModulePermissions';

const DEFAULT_PAGE_SIZE = 50;
const PAGE_SIZE_OPTIONS = [25, 50, 100];

/**
 * ==============================================================================
 * MAESTRO DE CÓDIGOS AFIP (WSFE)
 * ==============================================================================
 */
const AFIP_CBTE_TIPOS = {
  MONOTRIBUTO: {
    TODOS: { factura: 11, nota_debito: 12, nota_credito: 13 }
  },
  // Responsable Inscripto (y asimilables) - matriz WSFEv1
  RI: {
    RI: { factura: 1, nota_debito: 2, nota_credito: 3 },
    OTROS: { factura: 6, nota_debito: 7, nota_credito: 8 },
    EXTERIOR: { factura: 19 }
  }
};

const CBTE_TITULOS = {
  1: 'FACTURA A', 2: 'NOTA DE DÉBITO A', 3: 'NOTA DE CRÉDITO A',
  6: 'FACTURA B', 7: 'NOTA DE DÉBITO B', 8: 'NOTA DE CRÉDITO B',
  11: 'FACTURA C', 12: 'NOTA DE DÉBITO C', 13: 'NOTA DE CRÉDITO C',
  51: 'FACTURA M', 52: 'NOTA DE DÉBITO M', 53: 'NOTA DE CRÉDITO M'
};

const LEGACY_CRM_SOURCE_FILTER = 'crm_hito,crm_proyecto,crm_ticket,crm_schedule';
const buildFacturacionSiteFilter = (siteId) =>
  `site_id.eq.${siteId},and(site_id.is.null,billing_source.in.(${LEGACY_CRM_SOURCE_FILTER}))`;

const isMissingColumnError = (error, columnName) => {
  const needle = String(columnName || '').toLowerCase();
  const shortNeedle = needle.split('.').pop();
  const code = String(error?.code || error?.error?.code || '').toUpperCase();
  const message = [error?.message, error?.details, error?.hint]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  if (!needle) return false;

  return Boolean(
    code === '42703'
    || code === 'PGRST204'
    || message.includes(`column "${needle}"`)
    || message.includes(`column '${needle}'`)
    || message.includes(`column ${needle}`)
    || message.includes(`column "${shortNeedle}"`)
    || message.includes(`column '${shortNeedle}'`)
    || message.includes(`column ${shortNeedle}`)
    || message.includes(`could not find the '${needle}' column`)
    || message.includes(`could not find the "${needle}" column`)
    || message.includes(`could not find the '${shortNeedle}' column`)
    || message.includes(`could not find the "${shortNeedle}" column`)
  );
};

// Normaliza condiciones fiscales (evita variantes de texto)
const normCond = (v) => String(v || '').trim().toLowerCase();

const esMonotributo = (cond) => {
  const c = normCond(cond);
  return c.includes('monotrib');
};

const esRI = (cond) => {
  const c = normCond(cond);
  // Incluye Responsable Inscripto / IVA Responsable Inscripto
  return c.includes('responsable') && c.includes('inscrip');
};

const esExterior = (cond) => {
  const c = normCond(cond);
  return c.includes('exterior') || c.includes('export');
};

const esConsumidorFinal = (cond) => {
  const c = normCond(cond);
  return c.includes('consumidor') && c.includes('final');
};

const esExento = (cond) => {
  const c = normCond(cond);
  return c.includes('exento');
};

// Resolver códigos AFIP según matriz Emisor/Receptor
const resolverCodigosAFIP = (condEmisor, condReceptor) => {
  const em = normCond(condEmisor);
  const rc = normCond(condReceptor);

  // 1) Monotributo -> siempre C
  if (esMonotributo(em)) return AFIP_CBTE_TIPOS.MONOTRIBUTO.TODOS;

  // 2) Responsable Inscripto
  if (esRI(em)) {
    if (esExterior(rc)) return AFIP_CBTE_TIPOS.RI.EXTERIOR;
    if (esRI(rc) || esMonotributo(rc)) return AFIP_CBTE_TIPOS.RI.RI;
    // CF / Exento / resto -> B
    return AFIP_CBTE_TIPOS.RI.OTROS;
  }

  // 3) Fallback conservador
  return AFIP_CBTE_TIPOS.MONOTRIBUTO.TODOS;
};

// RG 5616 / WSFEv1: Condicion Frente al IVA del receptor (fallback razonable)
const mapCondicionIVAReceptorId = (condReceptor) => {
  const c = normCond(condReceptor);
  if (esRI(c)) return 1; // Responsable Inscripto
  if (esMonotributo(c)) return 6; // Monotributo
  if (esExento(c)) return 4; // Exento
  if (esExterior(c)) return 9; // Cliente Exterior
  if (esConsumidorFinal(c)) return 5; // Consumidor Final
  // fallback
  return 5;
};

export default function Facturacion() {
  const { profile, organization, refreshOrganization, user } = useAuth();
  const isSuperAdmin = String(user?.email || '').toLowerCase() === 'adpenia@gmail.com';

  // Venta administrativa (a cuenta corriente, fuera del arqueo). Admin la ve
  // siempre; el resto necesita el permiso facturacion-venta-administrativa.
  const [ventaAdminOpen, setVentaAdminOpen] = useState(false);
  const [effectiveModules, setEffectiveModules] = useState(null);
  const [pendingEmitSaleId, setPendingEmitSaleId] = useState(null);
  const puedeVentaAdministrativa = canCreateVentaAdministrativa({ profile, isSuperAdmin, effectiveModules });

  const [ventas, setVentas] = useState([]);
  const [sitios, setSitios] = useState([]);
  const [empresa, setEmpresa] = useState({});
  const [billingEntities, setBillingEntities] = useState([]);
  const [loading, setLoading] = useState(true);

  // Lógico de la organización (Datos de Empresa → Reglas operativas): con esto
  // encendido no se factura si el emisor no tiene tope cargado para el mes en
  // curso. `organization` viene del AuthContext; `empresa` es la copia local que
  // se hidrata en cargarDatos, y cubre el arranque antes del refresh.
  const requireMonthLimit = useMemo(
    () => Boolean(organization?.require_billing_entity_month_limit ?? empresa?.require_billing_entity_month_limit),
    [organization?.require_billing_entity_month_limit, empresa?.require_billing_entity_month_limit]
  );

  const [processingMap, setProcessingMap] = useState({});
  const [monthTotalsByEntity, setMonthTotalsByEntity] = useState({});
  // Ref espejo de monthTotalsByEntity para lectura sincrónica durante loops/clicks rápidos.
  // setState es asíncrono y la próxima validación leería el "spent" viejo, permitiendo
  // que pasen facturas que deberían bloquearse por límite.
  const monthTotalsRef = useRef({});
  const [monthKey, setMonthKey] = useState('');
  const [salesDateColumnsSupport, setSalesDateColumnsSupport] = useState({
    fechaVenta: null,
    fechaFacturacion: null,
  });
  const [saleErrorById, setSaleErrorById] = useState({});
  const [autoProcessing, setAutoProcessing] = useState(false);

  // Estados para filtro de métodos de pago
  const [metodosPago, setMetodosPago] = useState([]);
  const [filtroMetodosPago, setFiltroMetodosPago] = useState([]); // IDs habilitados
  const [showMetodosDropdown, setShowMetodosDropdown] = useState(false);

  // Estados para Modal de Impresión
  const [printModal, setPrintModal] = useState({ open: false, html: '' });
  const iframeRef = useRef(null);

  // Estado del popout de detalle de comprobante
  const [detailModal, setDetailModal] = useState({ open: false, venta: null, loading: false });
  const autoAssigningDefaultEntityRef = useRef(false);

  // --- FIX FECHA INICIAL (BUG 9PM) ---
  const getLocalToday = () => {
    const now = new Date();
    const offsetMs = now.getTimezoneOffset() * 60000;
    const localDate = new Date(now.getTime() - offsetMs);
    return localDate.toISOString().split('T')[0]; 
  };

  const getLocalFirstDay = () => {
    const now = new Date();
    // Primer día del mes actual
    const first = new Date(now.getFullYear(), now.getMonth(), 1);
    const offsetMs = first.getTimezoneOffset() * 60000;
    const localFirst = new Date(first.getTime() - offsetMs);
    return localFirst.toISOString().split('T')[0];
  };
  
  const [fechaVentaDesde, setFechaVentaDesde] = useState(getLocalFirstDay());
  const [fechaVentaHasta, setFechaVentaHasta] = useState(getLocalToday());
  const [fechaFacturacionDesde, setFechaFacturacionDesde] = useState('');
  const [fechaFacturacionHasta, setFechaFacturacionHasta] = useState('');
  const [filtroSitio, setFiltroSitio] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [filtroPendientes, setFiltroPendientes] = useState(false);
  const [savingConfigSuccess, setSavingConfigSuccess] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  // Configuración
  const [billingEntitySelected, setBillingEntitySelected] = useState('');

  // Modo de facturación: 'manual' (requiere botón) | 'auto' (procesa por prioridad)
  const [invoiceMode, setInvoiceMode] = useState('manual');

  // Modal NC
  const [ncModalOpen, setNcModalOpen] = useState(false);
  const [ncSelectedSale, setNcSelectedSale] = useState(null);

  // --- Helpers ---
  const toIsoStartOfDay = (dateStr) => {
    if (!dateStr) return null;
    return `${dateStr}T00:00:00.000Z`;
  };

  const toIsoEndOfDay = (dateStr) => {
    if (!dateStr) return null;
    return `${dateStr}T23:59:59.999Z`;
  };

  const fmtMoney = (n) => Number(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 });
  
  const getMonthKey = (d = new Date()) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    return `${y}-${m}`;
  };

  const monthRangeYMD = (monthKeyStr) => {
    const [y, m] = (monthKeyStr || '').split('-').map(Number);
    // Usamos UTC puro para rangos de mes
    const startYmd = `${y}-${String(m).padStart(2,'0')}-01`;
    // Calculamos ultimo día del mes
    const lastDay = new Date(y, m, 0).getDate();
    const endYmd = `${y}-${String(m).padStart(2,'0')}-${String(lastDay).padStart(2,'0')}`;
    return { startYmd, endYmd };
  };

  const monthRangeISO = (monthKeyStr) => {
    const { startYmd, endYmd } = monthRangeYMD(monthKeyStr);
    return {
      startIso: `${startYmd}T00:00:00.000Z`,
      endIso: `${endYmd}T23:59:59.999Z`,
    };
  };

  const normalizeCuit = (cuit) => String(cuit || '').replace(/\D/g, '');

  // Helpers para Ticket
  const escapeHtml = (str) => String(str ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  
  const formatDateAR = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleDateString('es-AR', { timeZone: 'UTC' });
  };

  const formatTimeAR = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleTimeString('es-AR', { timeZone: 'UTC', hour: '2-digit', minute:'2-digit' });
  };

  const formatDate = (value) => {
    if (!value) return '—';
    try {
      const d = (value instanceof Date) ? value : new Date(value);
      if (Number.isNaN(d.getTime())) return String(value);
      return d.toLocaleDateString('es-AR', { timeZone: 'UTC' }); // FIX VISUAL
    } catch {
      return String(value);
    }
  };

  const toDateOnly = (value) => {
    if (!value) return '';
    const raw = String(value);
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
    const [datePart] = raw.split('T');
    return /^\d{4}-\d{2}-\d{2}$/.test(datePart) ? datePart : raw;
  };

  const getSaleDate = (venta) => venta?.fecha_venta || toDateOnly(venta?.created_at);
  const getBillingDate = (venta) => venta?.fecha_facturacion || toDateOnly(venta?.invoice_sent_at) || getSaleDate(venta);
  const getFiscalDate = (venta) => ((venta?.invoice_status === 'facturado' || venta?.cae) ? getBillingDate(venta) : getSaleDate(venta));
  const getFiscalTimeSource = (venta) => ((venta?.invoice_status === 'facturado' || venta?.cae) ? (venta?.invoice_sent_at || venta?.created_at) : venta?.created_at);
  // Mes activo para LÍMITES por emisor: siempre el mes real actual.
  // Las facturas nuevas caen siempre en hoy, así que el cupo a chequear es el del mes
  // corriente, sin importar qué rango haya elegido el usuario en los filtros.
  const getActiveBillingMonthKey = () => getMonthKey(new Date());
  const setDateColumnSupport = (patch) => {
    setSalesDateColumnsSupport((prev) => ({ ...prev, ...patch }));
  };
  
  const resolveCbteTipo = (venta) => {
    const t1 = Number(venta?.invoice_cbte_tipo);
    const status = String(venta?.invoice_status || '').toLowerCase();
    const isFinal = status === 'facturado' || status === 'aprobado' || status === 'completed';
    if (Number.isFinite(t1) && t1 > 0 && isFinal) return t1;

    const t2 = Number(venta?.cbte_tipo);
    if (Number.isFinite(t2) && t2 > 0) return t2;

    try {
      const entityId = venta?.billing_entity_id || billingEntitySelected || '';
      const entity = (billingEntities || []).find((b) => b?.id === entityId) || null;

      const condEmisor = entity?.fiscal_configuration || entity?.tax_condition || empresa?.tax_condition || '';
      const condReceptor = venta?.clients?.tax_condition || 'Consumidor Final';
      const cods = resolverCodigosAFIP(condEmisor, condReceptor);
      const t3 = Number(cods?.factura);
      if (Number.isFinite(t3) && t3 > 0) {
        if (Number.isFinite(t1) && t1 > 0 && !isFinal) return t3;
        return t3;
      }
    } catch (e) {
      // silent fallback
    }

    if (Number.isFinite(t1) && t1 > 0) return t1;
    return 11;
  };

  const resolveCbteLabel = (venta) => {
    const lbl = String(venta?.invoice_type_label || '').trim();
    if (lbl) return lbl;
    const tipo = resolveCbteTipo(venta);
    return CBTE_TITULOS[tipo] || '-';
  };

  const registrarStatusHistory = async ({
    entityId,
    fieldName,
    fromValue,
    toValue,
    metadata = {}
  }) => {
    try {
      if (!profile?.organization_id || !entityId) return;
      await supabase.from('status_history').insert({
        organization_id: profile.organization_id,
        entity_type: 'sales',
        entity_id: entityId,
        field_name: fieldName,
        from_value: fromValue ?? null,
        to_value: toValue ?? null,
        changed_by: profile?.user_id || null,
        metadata: metadata || {}
      });
    } catch (e) {
      console.warn('status_history insert failed:', e?.message || e);
    }
  };

  const fetchErroresVentas = async (rows = []) => {
    try {
      if (!profile?.organization_id) return;
      // Solo tiene sentido buscar error_message para ventas en estado 'error'.
      // Filtrar acá evita URLs gigantes en el IN clause cuando hay miles de ventas.
      const errorIds = Array.from(new Set(
        (rows || [])
          .filter((r) => String(r?.invoice_status || '').toLowerCase() === 'error')
          .map((r) => r?.id)
          .filter(Boolean)
      ));
      if (errorIds.length === 0) {
        setSaleErrorById({});
        return;
      }

      // CHUNK 50 mantiene la URL bajo ~2 KB incluso con UUIDs encodeados.
      const CHUNK = 50;
      const map = {};

      for (let i = 0; i < errorIds.length; i += CHUNK) {
        const slice = errorIds.slice(i, i + CHUNK);
        const { data, error } = await supabase
          .from('status_history')
          .select('entity_id, changed_at, metadata')
          .eq('organization_id', profile.organization_id)
          .eq('entity_type', 'sales')
          .eq('field_name', 'invoice_status')
          .in('entity_id', slice)
          .order('changed_at', { ascending: false })
          .limit(500);
        if (error) throw error;

        (data || []).forEach((r) => {
          const id = r.entity_id;
          if (map[id]) return;
          const msg = r?.metadata?.error_message;
          if (msg) map[id] = String(msg);
        });
      }

      setSaleErrorById((prev) => ({ ...prev, ...map }));
    } catch (e) {
      // Loggear con contexto, no como Object pelado
      console.warn('[Facturacion] fetchErroresVentas falló:', e?.message || e, { code: e?.code, details: e?.details });
    }
  };

  // Actualiza billing_entity_id en lotes. PostgREST manda los ids en la URL
  // (?id=in.(...)); con miles de ventas la URL explota y el proxy la corta
  // (Cloudflare 520 / ERR_CONNECTION_CLOSED). Troceamos en chunks chicos.
  const ENTITY_UPDATE_CHUNK = 150;
  const updateEntityForSalesIds = async (ids, entityId, { onlyNullEntity = false } = {}) => {
    const uniqueIds = Array.from(new Set((ids || []).filter(Boolean)));
    for (let i = 0; i < uniqueIds.length; i += ENTITY_UPDATE_CHUNK) {
      const part = uniqueIds.slice(i, i + ENTITY_UPDATE_CHUNK);
      let q = supabase
        .from('sales')
        .update({ billing_entity_id: entityId, billing_source: 'manual' })
        .in('id', part);
      if (profile?.organization_id) q = q.eq('organization_id', profile.organization_id);
      if (onlyNullEntity) q = q.is('billing_entity_id', null);
      const { error } = await q;
      if (error) throw error;
    }
  };

  const asignarEmisorPorDefectoAVentas = async ({
    rows = [],
    defaultEntityId = billingEntitySelected,
    mode = invoiceMode,
  } = {}) => {
    try {
      if (!profile?.organization_id) return rows;
      if (mode !== 'manual') return rows;

      const entityId = String(defaultEntityId || '').trim();
      if (!entityId) return rows;
      if (autoAssigningDefaultEntityRef.current) return rows;

      const candidateIds = (rows || [])
        .filter((venta) => {
          const status = String(venta?.invoice_status || '').toLowerCase();
          return !venta?.billing_entity_id && (status === 'pendiente' || status === 'error');
        })
        .map((venta) => venta.id)
        .filter(Boolean);

      if (candidateIds.length === 0) return rows;

      autoAssigningDefaultEntityRef.current = true;

      await updateEntityForSalesIds(candidateIds, entityId, { onlyNullEntity: true });

      return (rows || []).map((venta) => (
        candidateIds.includes(venta.id)
          ? { ...venta, billing_entity_id: entityId, billing_source: 'manual' }
          : venta
      ));
    } catch (e) {
      console.warn('No se pudo autoasignar el emisor por defecto:', e?.message || e);
      return rows;
    } finally {
      autoAssigningDefaultEntityRef.current = false;
    }
  };

  // --- Efectos ---
  useEffect(() => {
    if (profile?.organization_id) {
      setMonthKey(getActiveBillingMonthKey());
      cargarMaestros();
      cargarDatos();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);

  useEffect(() => {
    setMonthKey(getActiveBillingMonthKey());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fechaFacturacionDesde, fechaFacturacionHasta]);

  useEffect(() => {
    if (profile?.organization_id) cargarDatos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fechaVentaDesde, fechaVentaHasta, fechaFacturacionDesde, fechaFacturacionHasta, filtroSitio]);

  useEffect(() => {
    if (!sitios.length) return;
    setFiltroSitio((prev) =>
      resolvePreferredSiteId(sitios, {
        currentSiteId: prev,
        fallbackSiteId: empresa?.print_config?.facturacion_sitio,
      }),
    );
  }, [sitios, empresa?.print_config?.facturacion_sitio]);

  useEffect(() => {
    if (filtroSitio) persistSelectedSiteId(filtroSitio);
  }, [filtroSitio]);

  useEffect(() => {
    if (!profile?.organization_id) return;
    if (invoiceMode !== 'manual') return;
    if (!billingEntitySelected) return;
    if (!ventas.length) return;

    let cancelled = false;

    const run = async () => {
      const nextVentas = await asignarEmisorPorDefectoAVentas({
        rows: ventas,
        defaultEntityId: billingEntitySelected,
        mode: invoiceMode,
      });

      if (!cancelled && nextVentas !== ventas) {
        setVentas(nextVentas);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.organization_id, invoiceMode, billingEntitySelected]);

  useEffect(() => {
    if (!profile?.organization_id) return;
    const interval = setInterval(async () => {
      try {
        const cutoff = new Date(Date.now() - 2 * 60 * 1000).toISOString();

        const { data: stuck, error } = await supabase
          .from('sales')
          .select('id')
          .eq('organization_id', profile.organization_id)
          .eq('invoice_status', 'enviado')
          .is('numero_factura', null)
          .is('cae', null)
          .lt('invoice_sent_at', cutoff);

        if (error) throw error;
        if (!stuck || stuck.length === 0) return;

        const ids = stuck.map((r) => r.id);
        const timeoutMsg = 'Timeout: quedó ENVIADO > 2 minutos sin CAE/Comprobante.';

        const { error: upErr } = await supabase
          .from('sales')
          .update({ invoice_status: 'error' })
          .in('id', ids)
          .eq('organization_id', profile.organization_id);
        if (upErr) throw upErr;

        try {
          const rows = ids.map((id) => ({
            organization_id: profile.organization_id,
            entity_type: 'sales',
            entity_id: id,
            field_name: 'invoice_status',
            from_value: 'enviado',
            to_value: 'error',
            changed_by: profile?.user_id || null,
            metadata: { error_message: timeoutMsg, reason: 'timeout' }
          }));
          await supabase.from('status_history').insert(rows);
          setSaleErrorById((prev) => {
            const next = { ...prev };
            ids.forEach((id) => { next[id] = timeoutMsg; });
            return next;
          });
        } catch (hh) {
          console.warn('No se pudo registrar status_history (timeout):', hh?.message || hh);
        }

        setVentas((prev) => (prev || []).map((v) => (ids.includes(v.id) ? { ...v, invoice_status: 'error' } : v)));
      } catch (e) {
        console.error('Watchdog facturación:', e);
      }
    }, 30000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.organization_id]);

  // --- Cargas ---
  useEffect(() => {
    if (!profile?.organization_id || !profile?.id) return undefined;
    // Admin no necesita la consulta: canCreateVentaAdministrativa ya concede.
    if (canCreateVentaAdministrativa({ profile, isSuperAdmin })) return undefined;
    let cancelled = false;
    fetchEffectiveModulePermissions({
      supabase,
      organizationId: profile.organization_id,
      userId: profile.id,
      roleName: profile.role,
    })
      .then(({ effective }) => { if (!cancelled) setEffectiveModules(effective); })
      .catch((e) => {
        console.error('No se pudieron resolver los permisos de facturación:', e);
        if (!cancelled) setEffectiveModules(null);
      });
    return () => { cancelled = true; };
  }, [profile?.organization_id, profile?.id, profile?.role, isSuperAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  const cargarMaestros = async () => {
    const orgId = profile.organization_id;
    const [sitiosRes, beRes, metodosRes] = await Promise.all([
      supabase.from('sitios').select('*').eq('organization_id', orgId).order('nombre'),
      supabase.from('billing_entities').select('*').eq('organization_id', orgId).order('is_active', { ascending: false }),
      supabase.from('payment_methods').select('*').eq('organization_id', orgId).eq('is_active', true)
    ]);
    setSitios(sitiosRes.data || []);
    setBillingEntities(beRes.data || []);
    
    const methods = metodosRes.data || [];
    setMetodosPago(methods);
    setFiltroMetodosPago(methods.map(m => m.id)); 
  };

  const cargarTotalesMes = async (mk = monthKey) => {
    try {
      if (!profile?.organization_id) return;
      const { startYmd, endYmd } = monthRangeYMD(mk || getMonthKey(new Date()));
      const { startIso, endIso } = monthRangeISO(mk || getMonthKey(new Date()));
      const useFechaFacturacion = salesDateColumnsSupport.fechaFacturacion !== false;

      const buildTotalsQuery = (useFf) => {
        let q = supabase.from('sales').select('billing_entity_id, total')
          .eq('organization_id', profile.organization_id)
          .eq('invoice_status', 'facturado');
        return useFf
          ? q.gte('fecha_facturacion', startYmd).lte('fecha_facturacion', endYmd)
          : q.gte('invoice_sent_at', startIso).lte('invoice_sent_at', endIso);
      };

      // Pagina en lotes para no quedar topado en las 1000 filas por defecto.
      const fetchAllTotals = async (useFf) => {
        const BATCH = 1000;
        const all = [];
        for (let offset = 0; ; offset += BATCH) {
          const { data, error } = await buildTotalsQuery(useFf).range(offset, offset + BATCH - 1);
          if (error) return { data: all, error };
          const batch = data || [];
          all.push(...batch);
          if (batch.length < BATCH) break;
        }
        return { data: all, error: null };
      };

      let { data, error } = await fetchAllTotals(useFechaFacturacion);
      if (error && useFechaFacturacion && isMissingColumnError(error, 'fecha_facturacion')) {
        setDateColumnSupport({ fechaFacturacion: false });
        const retry = await fetchAllTotals(false);
        data = retry.data;
        error = retry.error;
      }
      if (error) throw error;
      const totals = {};
      (data || []).forEach((r) => {
        const k = r.billing_entity_id || '__sin_cuit__';
        totals[k] = (totals[k] || 0) + Number(r.total || 0);
      });
      monthTotalsRef.current = totals;
      setMonthTotalsByEntity(totals);
    } catch (e) { console.error(e); }
  };

  const cargarDatos = async () => {
    setLoading(true);
    try {
      let resolvedInvoiceMode = invoiceMode;
      let resolvedDefaultEntityId = billingEntitySelected;

      // org se lee del AuthContext (cargado una sola vez por sesión)
      const org = organization;
      if (org) {
          setEmpresa(org);
          if (typeof org.auto_invoice_enabled === 'boolean') {
             resolvedInvoiceMode = org.auto_invoice_enabled ? 'auto' : 'manual';
             setInvoiceMode(resolvedInvoiceMode);
          }
          resolvedDefaultEntityId = org.default_billing_entity_id || '';
          setBillingEntitySelected(resolvedDefaultEntityId);
          if (org.print_config) {
             if (org.print_config.facturacion_metodos) setFiltroMetodosPago(org.print_config.facturacion_metodos);
          }
      }

      const saleStart = fechaVentaDesde || null;
      const saleEnd = fechaVentaHasta || null;
      const billingStart = fechaFacturacionDesde || null;
      const billingEnd = fechaFacturacionHasta || null;

      const fetchSalesRows = async (support = salesDateColumnsSupport, allowRetry = true) => {
        const useFechaVenta = support.fechaVenta !== false;
        const useFechaFacturacion = support.fechaFacturacion !== false;

        // Construye una query fresca por cada lote (los builders de PostgREST
        // no se pueden reutilizar tras aplicarles .range()).
        const buildQuery = () => {
          let query = supabase.from('sales')
            .select('*, clients(full_name, tax_condition, doc_type, doc_number, address, city, state), sitios(nombre)')
            .eq('organization_id', profile.organization_id)
            .eq('active', true)
            .neq('invoice_status', 'presupuesto')
            .order('created_at', { ascending: false });

          if (saleStart) {
            query = useFechaVenta
              ? query.gte('fecha_venta', saleStart)
              : query.gte('created_at', toIsoStartOfDay(saleStart));
          }
          if (saleEnd) {
            query = useFechaVenta
              ? query.lte('fecha_venta', saleEnd)
              : query.lte('created_at', toIsoEndOfDay(saleEnd));
          }
          if (billingStart) {
            query = useFechaFacturacion
              ? query.gte('fecha_facturacion', billingStart)
              : query.gte('invoice_sent_at', toIsoStartOfDay(billingStart));
          }
          if (billingEnd) {
            query = useFechaFacturacion
              ? query.lte('fecha_facturacion', billingEnd)
              : query.lte('invoice_sent_at', toIsoEndOfDay(billingEnd));
          }

          if (filtroSitio) query = query.or(buildFacturacionSiteFilter(filtroSitio));

          return query;
        };

        // Trae todas las filas que matchean el filtro paginando en lotes
        // (Supabase limita a 1000 filas por request por defecto).
        const BATCH = 1000;
        const all = [];
        for (let offset = 0; ; offset += BATCH) {
          const { data, error } = await buildQuery().range(offset, offset + BATCH - 1);

          if (error) {
            if (allowRetry) {
              const nextSupport = { ...support };
              let shouldRetry = false;

              if (isMissingColumnError(error, 'fecha_venta')) {
                nextSupport.fechaVenta = false;
                shouldRetry = true;
              }
              if (isMissingColumnError(error, 'fecha_facturacion')) {
                nextSupport.fechaFacturacion = false;
                shouldRetry = true;
              }

              if (shouldRetry) {
                setDateColumnSupport(nextSupport);
                return fetchSalesRows(nextSupport, false);
              }
            }
            throw error;
          }

          const batch = data || [];
          all.push(...batch);
          if (batch.length < BATCH) break;
        }

        return all;
      };

      const rows = await fetchSalesRows();
      const rowsWithDefaultEntity = await asignarEmisorPorDefectoAVentas({
        rows,
        defaultEntityId: resolvedDefaultEntityId,
        mode: resolvedInvoiceMode,
      });

      setVentas(rowsWithDefaultEntity);
      await fetchErroresVentas(rowsWithDefaultEntity);
      const mk = getActiveBillingMonthKey();
      setMonthKey(mk);
      await cargarTotalesMes(mk);
    } catch (e) {
      console.error(e);
      toast.error('Error sincronizando datos');
    } finally {
      setLoading(false);
    }
  };

  // --- Filtros & KPI ---
  const ventasFiltradas = useMemo(() => {
    const q = (busqueda || '').toLowerCase();
    return (ventas || []).filter((v) => {
      const c = String(v.clients?.full_name || '').toLowerCase();
      const id = String(v.id || '').toLowerCase();
      const nf = String(v.numero_factura || '').toLowerCase();
      const matchBusqueda = !q ? true : (c.includes(q) || id.includes(q) || nf.includes(q));
      const matchPendiente = filtroPendientes ? v.invoice_status !== 'facturado' : true;
      
      // Las ventas administrativas se cargan para facturarlas: no las oculta
      // el filtro de medios de pago habilitados.
      const matchMetodo = v.origen === VENTA_ADMINISTRATIVA_ORIGEN
        || (v.payment_method_id ? filtroMetodosPago.includes(v.payment_method_id) : true);

      return matchBusqueda && matchPendiente && matchMetodo;
    });
  }, [ventas, busqueda, filtroPendientes, filtroMetodosPago]);

  const getSortableValue = (venta, key) => {
    switch (key) {
      case 'created_at':
        return new Date(venta?.created_at || 0).getTime();
      case 'cliente':
        return String(venta?.clients?.full_name || 'Consumidor Final').toLowerCase();
      case 'estado':
        return String(venta?.invoice_status || '').toLowerCase();
      case 'tipo':
        return String(resolveCbteLabel(venta) || '').toLowerCase();
      case 'emisor': {
        const entity = resolverEmisorParaVentaSafe(venta);
        return String(entity?.tax_name || '').toLowerCase();
      }
      case 'numero_factura':
        return String(venta?.numero_factura || '').toLowerCase();
      case 'cae':
        return String(venta?.cae || '').toLowerCase();
      case 'fecha_facturacion':
        return new Date(getBillingDate(venta) || 0).getTime();
      case 'cae_vencimiento':
        return new Date(venta?.cae_vencimiento || 0).getTime();
      case 'total':
        return Number(venta?.total || 0);
      default:
        return '';
    }
  };

  const sortedVentasFiltradas = useMemo(() => {
    const items = [...ventasFiltradas];
    const { key, direction } = sortConfig;
    const directionFactor = direction === 'asc' ? 1 : -1;

    items.sort((a, b) => {
      const aValue = getSortableValue(a, key);
      const bValue = getSortableValue(b, key);

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return (aValue - bValue) * directionFactor;
      }

      return String(aValue).localeCompare(String(bValue), 'es-AR', { numeric: true, sensitivity: 'base' }) * directionFactor;
    });

    return items;
  }, [ventasFiltradas, sortConfig]);

  const totalPages = Math.max(1, Math.ceil(sortedVentasFiltradas.length / pageSize));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const pageStartIndex = (safeCurrentPage - 1) * pageSize;
  const pageEndIndex = Math.min(pageStartIndex + pageSize, sortedVentasFiltradas.length);
  const ventasPagina = useMemo(
    () => sortedVentasFiltradas.slice(pageStartIndex, pageEndIndex),
    [sortedVentasFiltradas, pageStartIndex, pageEndIndex]
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [busqueda, filtroPendientes, filtroMetodosPago, filtroSitio, fechaVentaDesde, fechaVentaHasta, fechaFacturacionDesde, fechaFacturacionHasta, sortConfig, pageSize]);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  const handleSort = (key) => {
    setSortConfig((prev) => (
      prev.key === key
        ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: key === 'created_at' || key === 'cae_vencimiento' || key === 'total' ? 'desc' : 'asc' }
    ));
  };

  const FilterHint = ({ text }) => (
    <span
      className="inline-flex h-4 w-4 items-center justify-center rounded-full border border-slate-200 bg-white text-[9px] font-black text-slate-400 cursor-help"
      title={text}
    >
      ?
    </span>
  );

  const SortableTh = ({ label, sortKey, align = 'left', title }) => {
    const isActive = sortConfig.key === sortKey;
    const Icon = isActive && sortConfig.direction === 'asc' ? ChevronUp : ChevronDown;
    const alignClass = align === 'center' ? 'justify-center text-center' : align === 'right' ? 'justify-end text-right' : 'justify-start text-left';

    return (
      <th className={`px-3.5 py-3 ${align === 'center' ? 'text-center' : align === 'right' ? 'text-right' : 'text-left'}`}>
        <button
          type="button"
          onClick={() => handleSort(sortKey)}
          title={title || `Ordenar por ${label}`}
          className={`inline-flex w-full items-center gap-1.5 font-black uppercase tracking-widest transition-colors ${alignClass} ${isActive ? 'text-slate-700' : 'text-slate-400 hover:text-slate-600'}`}
        >
          <span>{label}</span>
          <Icon size={12} className={isActive ? 'text-[#0B2A36]' : 'text-slate-300'} />
        </button>
      </th>
    );
  };

  const kpis = useMemo(() => {
    const totalVentas = ventasFiltradas.length;
    const pendientes = ventas.filter(v => v.invoice_status !== 'facturado').length;
    const montoFacturado = ventasFiltradas.filter(v => v.invoice_status === 'facturado').reduce((acc, v) => acc + Number(v.total || 0), 0);
    const notasCredito = ventasFiltradas.filter(v => v.invoice_status === 'anulada' || v.invoice_status === 'anulado_nc').length;
    return { totalVentas, pendientes, montoFacturado, notasCredito };
  }, [ventas, ventasFiltradas]);

  const findEntityById = (id) => (billingEntities || []).find((b) => b.id === id);

  function resolverEmisorParaVentaSafe(venta) {
    try {
      if (venta?.billing_entity_id) return findEntityById(venta.billing_entity_id) || null;
      if (billingEntitySelected) return findEntityById(billingEntitySelected) || null;
      return null;
    } catch {
      return null;
    }
  }

  const getEntityMonthLimit = (be, mk = monthKey) => getEntityMonthLimitUtil(be, mk);
  // Lee del ref (sincrónico) para evitar leer un valor stale entre clicks rápidos o
  // dentro del loop de auto-facturación.
  const getEntitySpent = (beId) => Number(monthTotalsRef.current?.[beId] ?? monthTotalsByEntity?.[beId] ?? 0);
  const getEntityRemaining = (be) => getEntityRemainingUtil(be, monthKey, getEntitySpent(be?.id));
  const activeEntities = useMemo(() => (billingEntities || []).filter((b) => b?.is_active !== false), [billingEntities]);
  const selectedBillingEntity = useMemo(
    () => (billingEntitySelected ? findEntityById(billingEntitySelected) || null : null),
    [billingEntities, billingEntitySelected]
  );
  const selectedEntityRemainingLabel = useMemo(() => {
    if (!selectedBillingEntity) return 'Sin emisor';
    // Con el lógico encendido, "Ilimitado" sería mentira: sin tope del mes no se
    // factura. El badge lo dice antes de que el usuario apriete Facturar.
    if (requireMonthLimit && !hasMonthLimitConfigured(selectedBillingEntity, monthKey)) {
      return `Falta límite ${monthKey}`;
    }
    const limit = getEntityMonthLimit(selectedBillingEntity);
    if (!limit || limit <= 0) return 'Ilimitado';
    return `$ ${fmtMoney(getEntityRemaining(selectedBillingEntity))}`;
  }, [selectedBillingEntity, monthTotalsByEntity, monthKey, requireMonthLimit]);

  const validarEmisorAntesDeFacturar = (be, montoVenta = 0) => {
    const r = validateBillingEntityForInvoice({
      entity: be,
      monthKey,
      spent: be?.id ? getEntitySpent(be.id) : 0,
      amount: montoVenta,
      requireMonthLimit,
    });
    if (r.ok) return { ok: true };

    const razones = {
      SIN_EMISOR: '⚠️ ATENCIÓN: Debés elegir un emisor.',
      EMISOR_INACTIVO: 'Emisor INACTIVO.',
      SIN_LIMITE_MES: `Este emisor no tiene límite cargado para ${monthKey}. Cargalo en Configuración → Facturación → Límites por mes, o desactivá "Exigir límite mensual del emisor" en Datos de Empresa.`,
      LIMITE_EXCEDIDO: `Límite mensual excedido para este emisor (${monthKey}). Disponible: $ ${fmtMoney(r.remaining)} • Venta: $ ${fmtMoney(r.amount)}`,
    };
    return { ok: false, code: r.code, reason: razones[r.code] || 'No se puede facturar con este emisor.' };
  };

  const actualizarEmisorVenta = async (saleId, value) => {
    try {
      if (!saleId) return;
      const nextId = value === '' ? null : value;
      const current = ventas.find((v) => v.id === saleId);
      
      if (current?.invoice_status === 'facturado') {
          return toast.error("No se puede editar una venta facturada");
      }
      
      const { error } = await supabase.from('sales').update({ billing_entity_id: nextId, billing_source: 'manual' }).eq('id', saleId);
      if (error) throw error;
      setVentas((prev) => prev.map((v) => (v.id === saleId ? { ...v, billing_entity_id: nextId, billing_source: 'manual' } : v)));
    } catch (e) { 
        console.error(e);
        toast.error("No se pudo actualizar el emisor"); 
    }
  };

  const aplicarEmisorATodos = async () => {
    if (!billingEntitySelected) return toast.error("Seleccioná primero un emisor en el desplegable superior.");
    const pendientesVisibles = ventasFiltradas.filter(v => v.invoice_status !== 'facturado');
    if (pendientesVisibles.length === 0) return toast.error("No hay ventas pendientes en la vista actual.");
    const confirm = window.confirm(`¿Asignar este emisor a las ${pendientesVisibles.length} ventas pendientes visibles?`);
    if (!confirm) return;
    const toastId = toast.loading("Asignando emisor...");
    try {
        const ids = pendientesVisibles.map(v => v.id);
        await updateEntityForSalesIds(ids, billingEntitySelected);
        setVentas(prev => prev.map(v => {
            if (ids.includes(v.id)) return { ...v, billing_entity_id: billingEntitySelected, billing_source: 'manual' };
            return v;
        }));
        toast.success("Emisor asignado correctamente", { id: toastId });
    } catch (e) {
        console.error(e);
        toast.error("Error al actualizar masivamente", { id: toastId });
    }
  };

  // =====================================================================
  //  GENERADOR URL AFIP (QR)
  // =====================================================================
  const generateAfipQrUrl = (venta) => {
    // CUIT del emisor que facturó (no el de la organización) y ptoVta/número del
    // comprobante: un QR armado con otro emisor apunta a un comprobante ajeno.
    const be = (billingEntities || []).find((b) => b.id === venta.billing_entity_id);
    return buildAfipQrUrl({
        sale: venta,
        emitterCuit: be?.cuit || empresa?.cuit,
        client: venta.clients || null,
        ptoVentaFallback: be?.afip_pto_venta,
    });
  };

  // =====================================================================
  //  MOTOR DE IMPRESIÓN DINÁMICO (RESPETA CONFIG REPORTES - COPIADO DE VENTASHISTORIAL)
  // =====================================================================
  const buildTicketHtml = (venta, qrImgData) => {
    // Motor único de impresión: src/utils/ticketFiscal.js (compartido con POS,
    // Historial y Pedidos web). Acá solo se elige la config y se resuelve el emisor.
    const printConfig = empresa.print_config || {};
    const isFiscal = venta.invoice_status === 'facturado' || !!venta.cae;
    const configKey = isFiscal ? 'factura' : 'ticket_venta';
    const config = printConfig[configKey] || {
        headerText: empresa.name || 'Comprobante',
        footerText: 'Gracias por su compra',
        showLogo: true,
        showClientInfo: true,
        showPaymentMethod: true,
    };
    const billingEntity = (billingEntities || []).find((b) => b.id === venta.billing_entity_id) || null;
    return buildFiscalTicketHtml({
        sale: venta,
        items: venta.sale_items || [],
        client: venta.clients || null,
        emitter: resolveTicketEmitter({ organization: empresa, billingEntity }),
        config,
        qrDataUrl: qrImgData,
        siteName: venta.sitios?.nombre || '',
    });
  };

  // Abre el popout de detalle con datos completos (items, productos, cliente, sitio)
  const abrirDetalleVenta = async (venta) => {
    if (!venta?.id) return;
    setDetailModal({ open: true, venta, loading: true });
    try {
      const { data: fullSale, error } = await supabase
        .from('sales')
        .select('*, sale_items(*, products!vinc_prod(name, iva_percentage)), clients(full_name, tax_condition, doc_type, doc_number, address, city, state, phone, email), sitios(nombre)')
        .eq('id', venta.id)
        .single();
      if (error) throw error;
      setDetailModal((prev) => (
        prev.open && prev.venta?.id === venta.id
          ? { open: true, venta: { ...prev.venta, ...fullSale }, loading: false }
          : prev
      ));
    } catch (e) {
      console.error('Error cargando detalle de venta:', e);
      setDetailModal((prev) => ({ ...prev, loading: false }));
      toast.error('No se pudo cargar el detalle');
    }
  };

  // ✅ FUNCIÓN DE IMPRESIÓN ACTUALIZADA (Carga items y genera QR)
  const handleImprimirNueva = async (ventaParcial) => {
    const toastId = toast.loading('Preparando impresión...');
    try {
        // Fetch full sale data (including items and products)
        const { data: fullSale, error } = await supabase
            .from('sales')
            .select(`
                *,
                sale_items(*, products!vinc_prod(name, iva_percentage, barcode)),
                clients(full_name, doc_type, doc_number, tax_condition, address, city),
                sitios(nombre)
            `)
            .eq('id', ventaParcial.id)
            .single();
        
        if (error) throw error;

        let qrData = null;
        // Generar QR solo si hay CAE
        if (fullSale.cae) {
            const url = generateAfipQrUrl(fullSale);
            if (url) {
                qrData = await QRCode.toDataURL(url, { errorCorrectionLevel: 'M', width: 150 });
            }
        }

        const html = buildTicketHtml(fullSale, qrData);
        setPrintModal({ open: true, html });
        toast.dismiss(toastId);
    } catch(e) {
        console.error(e);
        toast.error('Error al cargar datos del ticket', { id: toastId });
    }
  };

  // --- PDF & QR Legacy (Comentado, no se borra) ---
  /* const imprimirFacturaPDF = async (venta) => {
    // ... código antiguo comentado ...
  }; 
  */

  const facturarARCA = async (saleId, entity) => {
    const venta = (ventas || []).find(v => v.id === saleId);
    const cbteLbl = venta ? resolveCbteLabel(venta) : 'Comprobante';
    const toastId = toast.loading(`Enviando a AFIP (${cbteLbl})...`);
    const traceId = (globalThis.crypto && typeof globalThis.crypto.randomUUID === 'function')
      ? globalThis.crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

    try {
      if (!venta) { toast.dismiss(toastId); return; }

      const sentAtIso = new Date().toISOString();
      await supabase.from('sales')
        .update({ invoice_status: 'enviado', invoice_sent_at: sentAtIso, billing_entity_id: entity?.id || venta?.billing_entity_id || null })
        .eq('id', saleId);
      setVentas(prev => (prev || []).map(v => (v.id === saleId ? { ...v, invoice_status: 'enviado', invoice_sent_at: sentAtIso, billing_entity_id: entity?.id || v.billing_entity_id } : v)));
      await registrarStatusHistory({
        entityId: saleId,
        fieldName: 'invoice_status',
        fromValue: venta?.invoice_status || null,
        toValue: 'enviado',
        metadata: { trace_id: traceId, stage: 'frontend_mark_enviado', afip_mode: entity?.afip_mode || null }
      });

      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      const { data: saleItems } = await supabase
        .from('sale_items')
        .select('*, products!vinc_prod(iva_percentage)')
        .eq('sale_id', saleId);

      let projectUrl = supabase.supabaseUrl;
      if (projectUrl.endsWith('/')) projectUrl = projectUrl.slice(0, -1);
      const functionUrl = `${projectUrl}/functions/v1/arca-facturar`;

      const docNro = venta.clients?.doc_number || 0;
      const docTipo = venta.clients?.doc_type || 'DNI';

      const condEmisor = entity?.fiscal_configuration || entity?.tax_condition || empresa?.tax_condition || '';
      const condReceptor = venta?.clients?.tax_condition || 'Consumidor Final';
      const codigos = resolverCodigosAFIP(condEmisor, condReceptor);
      const cbteTipo = Number(codigos?.factura || resolveCbteTipo(venta) || 11);

      if (cbteTipo === 1 && String(docTipo || '').toUpperCase() !== 'CUIT') {
        throw new Error("Para FACTURA A el cliente debe tener DocTipo=CUIT (80). Revisá el legajo del cliente.");
      }

      const condicionIVAReceptorId = mapCondicionIVAReceptorId(condReceptor);

      try {
        if (!venta?.invoice_cbte_tipo) {
          await supabase
            .from('sales')
            .update({ invoice_cbte_tipo: cbteTipo, invoice_type_label: CBTE_TITULOS[cbteTipo] || null })
            .eq('id', saleId);
        }
      } catch (e) {
        // No bloquea la facturación
      }

      // IVA por alícuota: salvo comprobante C (monotributo), enviamos el desglose
      // explícito (ítem por ítem, agrupado por tasa) para que el CAE de AFIP
      // coincida exactamente con el ticket impreso.
      const esComprobanteC = cbteTipo === 11 || cbteTipo === 13;
      const billableItems = selectBillableItems(saleItems);
      const ivaBreakdown = esComprobanteC ? null : buildAfipIvaBreakdown(saleItems, { pricesIncludeIva: true, saleTotal: Number(venta.total) });

      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          sale_id: saleId,
          trace_id: traceId,
          billing_entity_id: entity?.id || venta?.billing_entity_id || null,
          cbte_tipo: cbteTipo,
          condicion_iva_receptor_id: condicionIVAReceptorId,
          condicion_iva_receptor: condReceptor,
          total: venta.total,
          prices_include_iva: true,
          cliente: { doc_tipo: docTipo, doc_nro: docNro, tax_condition: condReceptor },
          items: billableItems,
          ...(ivaBreakdown ? { iva: ivaBreakdown.iva, totals: ivaBreakdown.totals } : {})
        })
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.ok) {
        const err = new Error(data.message || 'Error desconocido');
        err.afip = data;
        err.http_status = response.status;
        throw err;
      }

      toast.success('AFIP aprobó el comprobante', { id: toastId });
      if (entity?.id) {
        // Sumamos al total mensual del emisor de forma sincrónica (ref) y en el estado.
        // El ref es crítico para que el siguiente click / iteración del loop auto
        // lea el "spent" actualizado antes de que React aplique el setState.
        const next = {
          ...monthTotalsRef.current,
          [entity.id]: Number(monthTotalsRef.current?.[entity.id] || 0) + Number(venta?.total || 0),
        };
        monthTotalsRef.current = next;
        setMonthTotalsByEntity(next);
      }
      setVentas((prev) => (prev || []).map((row) => (
        row.id === saleId
          ? {
              ...row,
              invoice_status: 'facturado',
              billing_entity_id: entity?.id || row.billing_entity_id,
              fecha_facturacion: toDateOnly(sentAtIso),
              invoice_sent_at: sentAtIso,
            }
          : row
      )));
      await cargarDatos();
      setSaleErrorById((prev) => {
        if (!prev?.[saleId]) return prev;
        const next = { ...prev };
        delete next[saleId];
        return next;
      });

    } catch (e) {
      console.error(`[ARCA][${traceId}] Error Facturación:`, e);
      try {
        const msg = String(e?.message || 'Error AFIP');
        await supabase.from('sales').update({ invoice_status: 'error' }).eq('id', saleId);
        await registrarStatusHistory({
          entityId: saleId,
          fieldName: 'invoice_status',
          fromValue: 'enviado',
          toValue: 'error',
          metadata: {
            trace_id: traceId,
            stage: 'frontend_error',
            error_message: msg,
            request: {
              sale_id: saleId,
              billing_entity_id: entity?.id || venta?.billing_entity_id || null
            }
          }
        });
        setVentas(prev => (prev || []).map(v => (v.id === saleId ? { ...v, invoice_status: 'error' } : v)));
        setSaleErrorById((prev) => ({ ...prev, [saleId]: msg }));
      } catch (ee) { console.error('No se pudo marcar ERROR:', ee); }

      if (e?.afip?.retryable) {
        toast.error('AFIP no respondió (problema de red, no rechazó el comprobante). Reintentá en unos segundos.', { id: toastId });
      } else {
        toast.error(`Error AFIP: ${e.message}`, { id: toastId });
      }
    }
  };

  // --- NC / Mock ---
  const handleNotaCreditoClick = (venta) => { setNcSelectedSale(venta); setNcModalOpen(true); };

  const confirmarNotaCredito = async () => {
    if (!ncSelectedSale) return;
    setNcModalOpen(false);
    const saleId = ncSelectedSale.id;
    const toastId = toast.loading('Generando NC en AFIP...');
    
    try {
        const entity = findEntityById(ncSelectedSale.billing_entity_id);
        const { data: { session } } = await supabase.auth.getSession();
        let projectUrl = supabase.supabaseUrl;
        if (projectUrl.endsWith('/')) projectUrl = projectUrl.slice(0, -1);
        
        // Mantenemos la lógica pero advertimos si el backend no soporta NC en modo hardcode
        toast.error("La función NC está deshabilitada mientras forzamos Factura C.");

    } catch (e) { toast.error(`Error: ${e.message}`, { id: toastId }); }
  };

  // "Emitir al guardar" de la venta administrativa: espera a que la venta
  // llegue en la recarga y recien ahi la manda a ARCA por el flujo normal.
  useEffect(() => {
    if (!pendingEmitSaleId || loading) return;
    const saleId = pendingEmitSaleId;
    setPendingEmitSaleId(null);
    if ((ventas || []).some((v) => v.id === saleId)) {
      facturarVenta(saleId);
    } else {
      toast('La venta quedó creada. Ajustá el filtro de fechas para verla y emitirla.', { icon: 'ℹ️' });
    }
  }, [pendingEmitSaleId, loading, ventas]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleVentaAdministrativaCreada = async (saleId, { emitir } = {}) => {
    setVentaAdminOpen(false);
    await cargarDatos();
    if (emitir && saleId) setPendingEmitSaleId(saleId);
  };

  const facturarVenta = async (saleId, entityOverride = null) => {
    const venta = (ventas || []).find(v => v.id === saleId);
    if (!venta || venta.invoice_status === 'facturado' || venta.invoice_status === 'anulado_nc' || processingMap[saleId]) return;
    if (!['pendiente', 'error'].includes(venta.invoice_status)) {
      return toast.error('La venta está en proceso (ENVIADO). Esperá 2 minutos o se marcará ERROR para reintentar.');
    }
    const entity = entityOverride || resolverEmisorParaVentaSafe(venta);
    const vEmisor = validarEmisorAntesDeFacturar(entity, venta?.total);
    if (!vEmisor.ok) return toast.error(vEmisor.reason);
    
    setProcessingMap(prev => ({ ...prev, [saleId]: true }));
    await facturarARCA(saleId, entity);
    setProcessingMap(prev => ({ ...prev, [saleId]: false }));
  };

  const resolverEmisorAutomatico = (venta) => {
    if (venta?.billing_entity_id) return findEntityById(venta.billing_entity_id) || null;

    const list = (billingEntities || [])
      .filter(b => b?.is_active)
      .sort((a, b) => Number(a?.priority ?? 999) - Number(b?.priority ?? 999));

    for (const be of list) {
      if (be?.is_blocking) continue;
      const validation = validarEmisorAntesDeFacturar(be, venta?.total);
      if (!validation.ok) continue;
      return be;
    }

    return null;
  };

  const guardarConfiguracion = async () => {
    try {
      if (!profile?.organization_id) return;
      const newAutoInvoice = invoiceMode === 'auto';
      const newDefaultEntity = billingEntitySelected || null;
      
      const toastId = toast.loading('Guardando configuración de facturación...');

      // print_config sale del AuthContext (sin refetch)
      const currentConfig = organization?.print_config || {};
      const newConfig = {
         ...currentConfig,
         facturacion_metodos: filtroMetodosPago,
         facturacion_sitio: filtroSitio
      };

      const { error } = await supabase.from('organizations').update({
        auto_invoice_enabled: newAutoInvoice,
        default_billing_entity_id: newDefaultEntity,
        print_config: newConfig
      }).eq('id', profile.organization_id);
      
      if (error) throw error;

      // Refrescamos la org del AuthContext (cargada una sola vez por sesión) para
      // que el nuevo default_billing_entity_id quede en memoria. Sin esto,
      // cargarDatos() vuelve a leer la copia stale y pisa billingEntitySelected
      // con el valor viejo, haciendo que el emisor por defecto "no se guarde".
      await refreshOrganization();

      toast.success('Configuración guardada correctamente', { id: toastId });

      setSavingConfigSuccess(true);
      setTimeout(() => setSavingConfigSuccess(false), 2000);
    } catch (e) {
      console.error(e);
      toast.error('No se pudo guardar la configuración');
    }
  };

  const facturarAutomaticamente = async () => {
    if (invoiceMode !== 'auto') return;
    if (autoProcessing) return;

    const pendientes = (ventasFiltradas || []).filter(v =>
      v?.invoice_status !== 'facturado' &&
      v?.invoice_status !== 'anulada' &&
      v?.invoice_status !== 'anulado_nc'
    );

    if (!pendientes.length) return toast('No hay comprobantes pendientes.', { icon: 'ℹ️' });

    setAutoProcessing(true);
    const toastId = toast.loading(`Facturando ${pendientes.length} comprobantes (AUTO)...`);
    try {
      for (const v of pendientes) {
        const entity = resolverEmisorAutomatico(v);
        if (!entity) {
          setSaleErrorById(prev => ({ ...prev, [v.id]: 'Sin emisor automático disponible' }));
          continue;
        }

        if (!v.billing_entity_id) {
          try {
            await supabase
              .from('sales')
              .update({ billing_entity_id: entity.id })
              .eq('id', v.id)
              .eq('organization_id', profile.organization_id);
            setVentas(prev => (prev || []).map(x => (x.id === v.id ? { ...x, billing_entity_id: entity.id } : x)));
          } catch (e) {
            console.error(e);
          }
        }

        await facturarVenta(v.id, entity);
      }

      toast.success('Auto-facturación finalizada.', { id: toastId });
    } catch (e) {
      console.error(e);
      toast.error('Error en auto-facturación.', { id: toastId });
    }
    setAutoProcessing(false);
  };

  if (loading && ventas.length === 0) {
    return (
      <div className="w-full min-h-[calc(100vh-24px)] bg-[#F8FAFC] p-4 flex items-center justify-center">
        <div className="flex items-center gap-3 text-slate-400">
          <RefreshCcw className="animate-spin" size={18} />
          <div className="text-[10px] font-black uppercase tracking-widest">Cargando Facturación...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full min-h-[calc(100vh-24px)] bg-[#F8FAFC] px-3 py-3 sm:px-4 sm:py-4 lg:px-5 font-sans text-slate-700 relative">
      
      <VentaAdministrativaModal
        open={ventaAdminOpen}
        onClose={() => setVentaAdminOpen(false)}
        onCreated={handleVentaAdministrativaCreada}
        profile={profile}
        organization={organization}
        sitios={sitios}
        defaultSiteId={filtroSitio || ''}
        billingEntityId={billingEntitySelected || organization?.default_billing_entity_id || null}
        billingEntityName={findEntityById(billingEntitySelected || organization?.default_billing_entity_id)?.tax_name || ''}
        effectiveModules={effectiveModules}
        isSuperAdmin={isSuperAdmin}
      />

      {/* Modal NC */}
      {ncModalOpen && ncSelectedSale && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md border border-slate-200 p-5 sm:p-6 relative">
            <button onClick={() => setNcModalOpen(false)} className="absolute top-4 right-4 text-slate-400 hover:text-slate-600"><X size={20} /></button>
            <div className="flex flex-col items-center text-center">
                <AlertTriangle className="text-rose-500 mb-4" size={32} />
                <h3 className="text-lg font-black text-slate-800 uppercase mb-2">¿Anular Factura?</h3>
                <p className="text-sm text-slate-500 mb-6">Se intentará generar una Nota de Crédito.</p>
                <div className="flex flex-col sm:flex-row gap-3 w-full">
                    <button onClick={() => setNcModalOpen(false)} className="flex-1 py-3 bg-white border rounded-xl text-slate-600 font-bold text-xs uppercase">Cancelar</button>
                    <button onClick={confirmarNotaCredito} className="flex-1 py-3 bg-rose-500 text-white rounded-xl font-bold text-xs uppercase">Confirmar</button>
                </div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="mb-3 flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 sm:w-11 sm:h-11 bg-[#0B2A36] rounded-2xl flex items-center justify-center shadow-md shrink-0">
            <Printer size={20} className="text-[#3FF2AA]" />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl md:text-2xl font-black text-slate-800 tracking-tight uppercase">Facturación</h1>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
              Control fiscal y electrónica.
            </p>
          </div>
        </div>
        <div className="flex w-full flex-col gap-2 sm:flex-row sm:flex-wrap lg:w-auto lg:justify-end">
          <div className="flex w-full items-center justify-between gap-2 bg-white/60 border border-slate-200 rounded-2xl px-3 py-2 shadow-sm sm:w-auto sm:justify-start">
            <span className="text-[9px] font-black uppercase tracking-widest text-slate-500">Modo</span>
            <select
              className="min-w-0 bg-white border border-slate-200 rounded-xl px-2 py-1 text-[10px] font-black uppercase tracking-widest text-slate-700"
              value={invoiceMode}
              onChange={(e) => setInvoiceMode(e.target.value)}
            >
              <option value="manual">Manual</option>
              <option value="auto">Automática</option>
            </select>
          </div>

          {invoiceMode === 'auto' && (
            <button
              onClick={facturarAutomaticamente}
              disabled={autoProcessing}
              className="w-full px-4 py-2.5 bg-[#0B2A36] text-white rounded-2xl shadow-lg hover:shadow-xl transition-all text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed sm:w-auto"
              title="Facturar automáticamente por prioridad de emisor"
            >
              {autoProcessing ? 'Procesando...' : 'Auto-Facturar'}
            </button>
          )}

          {puedeVentaAdministrativa && (
            <button
              onClick={() => setVentaAdminOpen(true)}
              className="w-full px-4 py-2.5 bg-white border border-slate-200 text-[#0B2A36] rounded-2xl shadow-sm hover:shadow-md transition-all text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2 sm:w-auto"
              title="Venta a cuenta corriente que no entra al arqueo de caja"
            >
              <FilePlus2 size={16} /> Venta administrativa
            </button>
          )}

          <button onClick={cargarDatos} className="w-full px-4 py-2.5 bg-[#3B82F6] text-white rounded-2xl shadow-lg hover:shadow-xl transition-all text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2 sm:w-auto">
            <RefreshCcw size={16} /> Actualizar
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-2 mb-3">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm px-3 py-2.5">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Pendientes</p>
            <AlertCircle size={14} className="text-amber-400" />
          </div>
          <p className="mt-1 text-lg font-black text-slate-800 tracking-tight leading-tight">{kpis.pendientes}</p>
          <p className="text-[9px] font-bold uppercase tracking-widest text-slate-400">Sin facturar</p>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm px-3 py-2.5">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Facturado Mes</p>
            <DollarSign size={14} className="text-emerald-400" />
          </div>
          <p className="mt-1 text-lg font-black text-slate-800 tracking-tight leading-tight">$ {fmtMoney(kpis.montoFacturado)}</p>
          <p className="text-[9px] font-bold uppercase tracking-widest text-slate-400">Total filtrado</p>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm px-3 py-2.5">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Notas Crédito</p>
            <Undo2 size={14} className="text-rose-400" />
          </div>
          <p className="mt-1 text-lg font-black text-slate-800 tracking-tight leading-tight">{kpis.notasCredito}</p>
          <p className="text-[9px] font-bold uppercase tracking-widest text-slate-400">Anulaciones</p>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm px-3 py-2.5">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Total Ops</p>
            <Layers size={14} className="text-slate-300" />
          </div>
          <p className="mt-1 text-lg font-black text-slate-800 tracking-tight leading-tight">{kpis.totalVentas}</p>
          <p className="text-[9px] font-bold uppercase tracking-widest text-slate-400">Transacciones</p>
        </div>
      </div>

      {/* FILTROS Y CONFIG */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-3 mb-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-12 gap-3">

          {/* BUSCADOR */}
          <div className="sm:col-span-2 xl:col-span-4">
            <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-1">
              <Search size={12} /> Buscar <FilterHint text="Filtra por cliente, identificador interno o número de comprobante." />
            </label>
            <div className="relative mt-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" size={16} />
              <input
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                className="w-full pl-10 pr-3 py-2.5 bg-slate-50 border border-slate-100 rounded-xl text-[11px] font-bold uppercase outline-none focus:border-[#51A7FD] focus:bg-white transition-all shadow-inner"
                placeholder="Cliente, N°, CAE..."
              />
            </div>
          </div>

          {/* FECHA VENTA */}
          <div className="xl:col-span-4">
             <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-1">
              <Calendar size={12} /> Fecha venta <FilterHint text="Filtra por el rango de fecha de venta operativa." />
            </label>
            <div className="mt-1 flex items-center gap-1 bg-slate-50 border border-slate-100 rounded-xl px-2 py-2 shadow-inner">
                <input type="date" value={fechaVentaDesde} onChange={(e) => setFechaVentaDesde(e.target.value)} className="min-w-0 bg-transparent border-none text-[10px] font-bold text-slate-600 outline-none w-full" />
                <span className="text-slate-300">-</span>
                <input type="date" value={fechaVentaHasta} onChange={(e) => setFechaVentaHasta(e.target.value)} className="min-w-0 bg-transparent border-none text-[10px] font-bold text-slate-600 outline-none w-full" />
            </div>
          </div>

          {/* FECHA FACTURACIÓN */}
          <div className="xl:col-span-4">
             <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-1">
              <Calendar size={12} /> Fecha facturación <FilterHint text="Opcional. Filtra por el rango en que se emitió la factura." />
            </label>
            <div className="mt-1 flex items-center gap-1 bg-slate-50 border border-slate-100 rounded-xl px-2 py-2 shadow-inner">
                <input type="date" value={fechaFacturacionDesde} onChange={(e) => setFechaFacturacionDesde(e.target.value)} className="min-w-0 bg-transparent border-none text-[10px] font-bold text-slate-600 outline-none w-full" />
                <span className="text-slate-300">-</span>
                <input type="date" value={fechaFacturacionHasta} onChange={(e) => setFechaFacturacionHasta(e.target.value)} className="min-w-0 bg-transparent border-none text-[10px] font-bold text-slate-600 outline-none w-full" />
            </div>
          </div>

          {/* SELECTOR EMISOR GLOBAL */}
          <div className="sm:col-span-2 xl:col-span-12">
            <div className="flex items-center ml-1 mb-1">
                <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1" title="Emisor usado por defecto para Auto-Facturar">
                  <Filter size={12} /> Emisor por defecto <FilterHint text="En modo manual se asigna automáticamente a las ventas pendientes sin emisor y también sirve para la asignación masiva visible." />
                </label>
            </div>
            <div className="flex gap-1">
                <select
                    value={billingEntitySelected}
                    onChange={(e) => setBillingEntitySelected(e.target.value)}
                    className="w-full bg-slate-50 border border-slate-100 px-3 py-2.5 rounded-xl text-[10px] font-bold uppercase outline-none focus:border-[#51A7FD] transition-all cursor-pointer shadow-inner"
                >
                    <option value="">-- SIN ASIGNAR --</option>
                    {(activeEntities || []).map(be => (
                        <option key={be.id} value={be.id}>{be.tax_name}</option>
                    ))}
                </select>
                {billingEntitySelected && (() => {
                    const faltaLimite = requireMonthLimit && !!selectedBillingEntity && !hasMonthLimitConfigured(selectedBillingEntity, monthKey);
                    return (
                    <div
                        className={`px-3 py-2.5 border rounded-xl shadow-inner flex flex-col justify-center ${faltaLimite ? 'bg-rose-50 border-rose-200' : 'bg-slate-50 border-slate-100'}`}
                        title={faltaLimite
                          ? `Facturación bloqueada: cargá el límite de ${monthKey} para este emisor en Configuración → Facturación → Límites por mes.`
                          : `Disponible del emisor seleccionado para ${monthKey || 'el mes activo'}`}
                    >
                        <span className={`text-[8px] font-black uppercase tracking-widest whitespace-nowrap ${faltaLimite ? 'text-rose-500' : 'text-slate-400'}`}>Límite restante</span>
                        <span className={`text-[11px] font-black whitespace-nowrap ${faltaLimite ? 'text-rose-600' : 'text-slate-700'}`}>{selectedEntityRemainingLabel}</span>
                    </div>
                    );
                })()}
                {invoiceMode === 'manual' && billingEntitySelected && (
                    <button
                        onClick={aplicarEmisorATodos}
                        className="px-4 py-2.5 bg-blue-50 text-blue-600 border border-blue-100 rounded-xl hover:bg-blue-100 transition-all shadow-sm text-[10px] font-black uppercase tracking-widest whitespace-nowrap"
                        title="Aplicar este emisor a todas las ventas pendientes visibles"
                    >
                        Aplicar
                    </button>
                )}
            </div>
          </div>
        </div>

        {/* Filtros secundarios */}
        <div className="mt-3 flex items-center gap-2 flex-wrap">
            <button
                onClick={() => setFiltroPendientes(!filtroPendientes)}
                className={`px-4 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all ${
                    filtroPendientes ? 'bg-amber-100 text-amber-800 border border-amber-200' : 'bg-slate-50 text-slate-400 border border-slate-100 hover:text-slate-600'
                }`}
            >
                {filtroPendientes ? 'Viendo Pendientes' : 'Ver Todos'}
            </button>

            <select
                value={filtroSitio}
                onChange={(e) => setFiltroSitio(e.target.value)}
                className="px-4 py-2 rounded-xl bg-slate-50 border border-slate-100 text-slate-500 text-[9px] font-black uppercase tracking-widest outline-none"
            >
                <option value="">Todas las Sucursales</option>
                {sitios.map(s => <option key={s.id} value={s.id}>{s.nombre}</option>)}
            </select>

            {/* Filtro Métodos de Pago */}
            <div className="relative">
              <button
                onClick={() => setShowMetodosDropdown(!showMetodosDropdown)}
                className="px-4 py-2 rounded-xl bg-slate-50 border border-slate-100 text-slate-500 text-[9px] font-black uppercase tracking-widest outline-none flex items-center gap-2 hover:text-slate-700"
                title="Elegí qué métodos participan en esta vista de facturación."
              >
                <CreditCard size={14} />
                Métodos de Cobro
                <ChevronDown size={12} />
              </button>
              {showMetodosDropdown && (
                <div className="absolute top-full left-0 mt-2 w-56 bg-white border border-slate-200 rounded-xl shadow-xl z-50 p-2">
                  <div className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-2 px-2">Habilitar Facturación</div>
                  {metodosPago.map(m => (
                    <label key={m.id} className="flex items-center gap-2 p-2 hover:bg-slate-50 rounded-lg cursor-pointer">
                      <input
                        type="checkbox"
                        checked={filtroMetodosPago.includes(m.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFiltroMetodosPago(prev => [...prev, m.id]);
                          } else {
                            setFiltroMetodosPago(prev => prev.filter(id => id !== m.id));
                          }
                        }}
                        className="rounded border-slate-300 text-[#0B2A36] focus:ring-0 w-3 h-3"
                      />
                      <span className="text-[10px] font-bold text-slate-700 uppercase">{m.name}</span>
                    </label>
                  ))}
                  <div className="border-t border-slate-100 mt-2 pt-2 flex justify-between px-1">
                     <button onClick={() => setFiltroMetodosPago(metodosPago.map(m => m.id))} className="text-[8px] text-blue-500 font-bold uppercase">Todos</button>
                     <button onClick={() => setFiltroMetodosPago([])} className="text-[8px] text-rose-500 font-bold uppercase">Ninguno</button>
                  </div>
                </div>
              )}
            </div>

            <div className="w-full sm:w-auto sm:ml-auto">
               <button
                  onClick={guardarConfiguracion}
                  title="Guarda el modo, emisor por defecto y filtros persistentes de esta pantalla."
                  className={`w-full sm:w-auto px-5 py-2.5 rounded-xl shadow-md transition-all text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2
                    ${savingConfigSuccess ? 'bg-[#3FF2AA] text-[#0B2A36] scale-105' : 'bg-[#10B981] text-white hover:bg-[#059669] hover:shadow-lg'}`}
               >
                  {savingConfigSuccess ? <Check size={16} /> : <Settings size={16} />}
                  {savingConfigSuccess ? '¡Guardado!' : 'Guardar Configuración'}
               </button>
            </div>
        </div>
      </div>

      {/* Tabla (Dark Header Style) */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="bg-[#0B2A36] px-4 py-2.5 sm:px-5 border-b border-white/10 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2">
            <FileText size={16} className="text-[#3FF2AA]" />
            <span className="text-[10px] font-black text-white uppercase tracking-widest">
              Comprobantes ({sortedVentasFiltradas.length})
            </span>
          </div>
        </div>

        <div className="lg:hidden p-3 space-y-2.5 bg-slate-50/40">
          {sortedVentasFiltradas.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-200 bg-white px-4 py-10 text-center text-[10px] font-black uppercase tracking-widest text-slate-300">
              Sin registros
            </div>
          ) : (
            ventasPagina.map((v) => {
              const isFacturada = v.invoice_status === 'facturado';
              const isAnulada = v.invoice_status === 'anulado_nc';
              const isProc = !!processingMap[v.id];
              const entityRow = resolverEmisorParaVentaSafe(v);
              const vEmisorRow = validarEmisorAntesDeFacturar(entityRow, v?.total);
              const canFacturar = !isProc && !isFacturada && !isAnulada && ['pendiente','error'].includes(v.invoice_status) && vEmisorRow.ok;
              const canEditEmisor = v.invoice_status === 'pendiente' || v.invoice_status === 'error';

              return (
                <div
                  key={v.id}
                  className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm cursor-pointer hover:border-slate-300 transition-all"
                  onClick={() => abrirDetalleVenta(v)}
                  title="Ver detalle del comprobante"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-[11px] font-black uppercase text-slate-800 truncate">{v.clients?.full_name || 'Consumidor Final'}{v.origen === VENTA_ADMINISTRATIVA_ORIGEN && <span className="ml-1.5 align-middle px-1.5 py-0.5 rounded-md bg-violet-50 text-violet-600 text-[8px] font-black tracking-widest" title="Venta administrativa: fuera del arqueo de caja">ADMIN</span>}</div>
                      <div className="mt-1 text-[9px] font-bold uppercase tracking-widest text-slate-400">
                        {new Date(v.created_at).toLocaleDateString('es-AR', { timeZone: 'UTC' })} · {new Date(v.created_at).toLocaleTimeString('es-AR', { timeZone: 'UTC', hour:'2-digit', minute:'2-digit'})}
                      </div>
                    </div>
                    <span
                      title={v.invoice_status === 'error' ? (saleErrorById?.[v.id] || 'Error sin detalle') : ''}
                      className={`shrink-0 px-2 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest border ${
                        isFacturada ? 'bg-emerald-50 text-emerald-600 border-emerald-100' :
                        isAnulada ? 'bg-rose-50 text-rose-600 border-rose-100' :
                        v.invoice_status === 'enviado' ? 'bg-blue-50 text-blue-600 border-blue-100' :
                        v.invoice_status === 'error' ? 'bg-rose-50 text-rose-600 border-rose-100' :
                        'bg-amber-50 text-amber-600 border-amber-100'
                      }`}
                    >
                      {isAnulada ? 'ANULADA' : v.invoice_status}
                    </span>
                  </div>

                  <div className="mt-3 grid grid-cols-2 gap-2 text-[10px]">
                    <div className="rounded-xl bg-slate-50 px-3 py-2">
                      <div className="text-[8px] font-black uppercase tracking-widest text-slate-400">Tipo</div>
                      <div className="mt-1 font-black text-slate-700 uppercase">{resolveCbteLabel(v) || '-'}</div>
                    </div>
                    <div className="rounded-xl bg-slate-50 px-3 py-2 text-right">
                      <div className="text-[8px] font-black uppercase tracking-widest text-slate-400">Total</div>
                      <div className="mt-1 font-black text-slate-900">$ {fmtMoney(v.total)}</div>
                    </div>
                    <div className="col-span-2 rounded-xl bg-slate-50 px-3 py-2" onClick={(e) => e.stopPropagation()}>
                      <div className="text-[8px] font-black uppercase tracking-widest text-slate-400">Emisor</div>
                      <div className="mt-1">
                        <select
                          className={`w-full p-2 rounded-lg text-[9px] font-bold uppercase shadow-sm border ${
                            !v.billing_entity_id && canEditEmisor ? 'bg-amber-50 border-amber-300 text-amber-800' : 'bg-white border-slate-200 text-slate-600'
                          }`}
                          value={v.billing_entity_id || ''}
                          onChange={(e) => actualizarEmisorVenta(v.id, e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          disabled={!canEditEmisor || isProc}
                          title="Emisor fiscal asociado a esta venta"
                        >
                          <option value="">- Seleccionar -</option>
                          {activeEntities.map(b => {
                            const rem = getEntityRemaining(b);
                            const limit = getEntityMonthLimit(b);
                            // Con el lógico encendido, un emisor sin tope del mes no puede
                            // facturar: se avisa en la propia opción para no elegirlo a ciegas.
                            const sinLimiteMes = requireMonthLimit && !hasMonthLimitConfigured(b, monthKey);
                            const tag = sinLimiteMes ? `(sin límite ${monthKey})` : (limit > 0) ? `($${fmtMoney(rem)})` : '';
                            return <option key={b.id} value={b.id}>{(b.tax_name).slice(0,10)}... {tag}</option>;
                          })}
                        </select>
                      </div>
                    </div>
                    <div className="col-span-2 rounded-xl bg-slate-50 px-3 py-2">
                      <div className="text-[8px] font-black uppercase tracking-widest text-slate-400">Nro. Comp.</div>
                      <div className="mt-0.5 font-bold text-slate-600">{v.numero_factura || '-'}</div>
                    </div>
                  </div>

                  <div className="mt-3 flex flex-wrap gap-2" onClick={(e) => e.stopPropagation()}>
                    {v.invoice_status === 'error' && !isFacturada && !isAnulada && (
                      <button
                        onClick={() => facturarVenta(v.id)}
                        disabled={!vEmisorRow.ok || isProc}
                        title={!vEmisorRow.ok ? vEmisorRow.reason : 'Reintentar facturación'}
                        className={`flex-1 min-w-[120px] px-3 py-2 rounded-xl shadow transition-all border text-[10px] font-black uppercase tracking-widest ${
                          (!vEmisorRow.ok || isProc)
                            ? 'bg-slate-100 text-slate-300 border-slate-200 cursor-not-allowed'
                            : 'bg-rose-500 text-white border-rose-500 hover:opacity-90'
                        }`}
                      >
                        {isProc ? 'Reintentando...' : 'Reintentar'}
                      </button>
                    )}

                    {!isFacturada && !isAnulada && (
                      <button
                        onClick={() => facturarVenta(v.id)}
                        disabled={!canFacturar}
                        title={!vEmisorRow.ok ? vEmisorRow.reason : 'Facturar comprobante'}
                        className={`flex-1 min-w-[120px] px-3 py-2 rounded-xl shadow transition-all border text-[10px] font-black uppercase tracking-widest ${
                          canFacturar
                            ? 'bg-[#0B2A36] text-white border-[#0B2A36] hover:opacity-90'
                            : 'bg-slate-100 text-slate-300 border-slate-200 cursor-not-allowed'
                        }`}
                      >
                        {isProc ? 'Procesando...' : 'Facturar'}
                      </button>
                    )}

                    {(isFacturada || isAnulada) && (
                      <button
                        onClick={() => handleImprimirNueva(v)}
                        title="Abrir vista previa para imprimir"
                        className="flex-1 min-w-[120px] px-3 py-2 bg-white border border-slate-200 text-slate-600 rounded-xl hover:text-[#51A7FD] transition-all shadow-sm text-[10px] font-black uppercase tracking-widest"
                      >
                        Imprimir
                      </button>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div className="hidden lg:block w-full">
          <table className="w-full text-left table-fixed">
            <thead className="bg-slate-50/60 border-b border-slate-100 text-[9px] font-black uppercase tracking-widest text-slate-400">
              <tr>
                <SortableTh label="Fecha venta" sortKey="created_at" title="Ordenar por fecha y hora de la venta" />
                <SortableTh label="Cliente" sortKey="cliente" title="Ordenar por cliente" />
                <SortableTh label="Estado" sortKey="estado" align="center" title="Ordenar por estado fiscal" />
                <SortableTh label="Tipo" sortKey="tipo" align="center" title="Ordenar por tipo de comprobante" />
                <SortableTh label="Emisor" sortKey="emisor" align="center" title="Ordenar por emisor asignado" />
                <SortableTh label="Nro. Comp." sortKey="numero_factura" align="center" title="Ordenar por numero de comprobante" />
                <SortableTh label="Total" sortKey="total" align="right" title="Ordenar por importe total" />
                <th className="px-2 py-3 text-center w-[110px]">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {sortedVentasFiltradas.length === 0 ? (
                 <tr><td colSpan="8" className="py-12 text-center text-[10px] font-black uppercase text-slate-300">Sin registros</td></tr>
              ) : (
                ventasPagina.map((v) => {
                  const isFacturada = v.invoice_status === 'facturado';
                  const isAnulada = v.invoice_status === 'anulado_nc';
                  const isProc = !!processingMap[v.id];
                  const entityRow = resolverEmisorParaVentaSafe(v);
                  const vEmisorRow = validarEmisorAntesDeFacturar(entityRow, v?.total);
                  const canFacturar = !isProc && !isFacturada && !isAnulada && ['pendiente','error'].includes(v.invoice_status) && vEmisorRow.ok;

                  // Solo permitimos editar emisor si está pendiente o error
                  const canEditEmisor = v.invoice_status === 'pendiente' || v.invoice_status === 'error';
                  const stop = (e) => e.stopPropagation();

                  return (
                    <tr
                      key={v.id}
                      className="hover:bg-slate-50 transition-all cursor-pointer"
                      onClick={() => abrirDetalleVenta(v)}
                      title="Ver detalle del comprobante"
                    >
                      <td className="px-4 py-3">
                         {/* FIX: Usamos UTC para visualizar correctamente las fechas forzadas */}
                         <div className="text-[11px] font-bold text-slate-700">{new Date(v.created_at).toLocaleDateString('es-AR', { timeZone: 'UTC' })}</div>
                         <div className="text-[9px] font-bold text-slate-400">{new Date(v.created_at).toLocaleTimeString('es-AR', { timeZone: 'UTC', hour:'2-digit', minute:'2-digit'})}</div>
                      </td>
                      <td className="px-3.5 py-3 text-[11px] font-black text-slate-800 uppercase truncate">{v.clients?.full_name || 'Consumidor Final'}{v.origen === VENTA_ADMINISTRATIVA_ORIGEN && <span className="ml-1.5 align-middle px-1.5 py-0.5 rounded-md bg-violet-50 text-violet-600 text-[8px] font-black tracking-widest" title="Venta administrativa: fuera del arqueo de caja">ADMIN</span>}</td>
                      <td className="px-3.5 py-3 text-center">
                          <span
                            title={v.invoice_status === 'error' ? (saleErrorById?.[v.id] || 'Error sin detalle') : ''}
                            className={`px-2 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest border ${
                              isFacturada ? 'bg-emerald-50 text-emerald-600 border-emerald-100' :
                              isAnulada ? 'bg-rose-50 text-rose-600 border-rose-100' :
                              v.invoice_status === 'enviado' ? 'bg-blue-50 text-blue-600 border-blue-100' :
                              v.invoice_status === 'error' ? 'bg-rose-50 text-rose-600 border-rose-100' :
                              'bg-amber-50 text-amber-600 border-amber-100'
                          }`}
                          >
                              {isAnulada ? 'ANULADA' : v.invoice_status}
                          </span>
                      </td>

                      {/* ✅ NUEVO: Tipo de comprobante */}
                      <td className="px-3.5 py-3 text-center">
                        <div className="text-[10px] font-black text-slate-700 uppercase">
                          {resolveCbteLabel(v) || '-'}
                        </div>
                        <div className="text-[9px] font-bold text-slate-400">
                          {String(resolveCbteTipo(v) || '').trim() ? `COD ${resolveCbteTipo(v)}` : ''}
                        </div>
                      </td>

                      {/* SELECTOR POR FILA (Solo editable si pendiente/error) */}
                      <td className="px-3.5 py-3 text-center" onClick={stop}>
                          <select
                            className={`p-1.5 rounded-lg text-[9px] font-bold uppercase shadow-sm border max-w-[140px] ${
                                !v.billing_entity_id && canEditEmisor ? 'bg-amber-50 border-amber-300 text-amber-800' : 'bg-white border-slate-200 text-slate-600'
                            }`}
                            value={v.billing_entity_id || ''}
                            onChange={(e) => actualizarEmisorVenta(v.id, e.target.value)}
                            onClick={stop}
                            disabled={!canEditEmisor || isProc}
                          >
                            <option value="">- Seleccionar -</option>
                            {activeEntities.map(b => (
                                <option key={b.id} value={b.id}>{b.tax_name}</option>
                            ))}
                          </select>
                      </td>

                      <td className="px-3.5 py-3 text-center text-[10px] font-bold text-slate-600">{v.numero_factura || '-'}</td>
                      <td className="px-3.5 py-3 text-right text-[11px] font-black text-slate-900">$ {fmtMoney(v.total)}</td>
                      <td className="px-2 py-3 text-center" onClick={stop}>
                        <div className="flex justify-center gap-1.5">
                           {/* REINTENTAR (Solo si está en error) */}
                           {v.invoice_status === 'error' && !isFacturada && !isAnulada && (
                             <button
                               onClick={(e) => { stop(e); facturarVenta(v.id); }}
                               disabled={!vEmisorRow.ok || isProc}
                               title={!vEmisorRow.ok ? vEmisorRow.reason : 'Reintentar facturación'}
                               className={`p-2 rounded-lg shadow transition-all border ${
                                 (!vEmisorRow.ok || isProc)
                                   ? 'bg-slate-100 text-slate-300 border-slate-200 cursor-not-allowed'
                                   : 'bg-rose-500 text-white border-rose-500 hover:opacity-90'
                               }`}
                             >
                               <RefreshCcw size={14} />
                             </button>
                           )}

                           {/* FACTURAR (Solo si no está ya hecha) */}
                           {!isFacturada && !isAnulada && (
                            <button
                              onClick={(e) => { stop(e); facturarVenta(v.id); }}
                              disabled={!canFacturar}
                              title={!vEmisorRow.ok ? vEmisorRow.reason : 'Facturar'}
                              className={`px-3 py-2 rounded-lg shadow transition-all border text-[10px] font-black uppercase tracking-widest ${
                                canFacturar
                                  ? 'bg-[#0B2A36] text-white border-[#0B2A36] hover:opacity-90'
                                  : 'bg-slate-100 text-slate-300 border-slate-200 cursor-not-allowed'
                              }`}
                            >
                                {isProc ? '…' : 'Facturar'}
                            </button>
                           )}

                           {/* IMPRIMIR (Solo si está hecha) — icono compacto */}
                           {(isFacturada || isAnulada) && (
                               <button
                                 onClick={(e) => { stop(e); handleImprimirNueva(v); }}
                                 className="p-2 bg-white border border-slate-200 text-slate-500 rounded-lg hover:text-[#51A7FD] hover:border-[#51A7FD]/40 transition-all shadow-sm"
                                 title="Imprimir comprobante"
                               >
                                 <Printer size={14} />
                               </button>
                           )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {sortedVentasFiltradas.length > 0 && (
          <div className="border-t border-slate-100 bg-white px-3 py-3 sm:px-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-[9px] font-black uppercase tracking-widest text-slate-400 text-center sm:text-left">
              Mostrando {pageStartIndex + 1}-{pageEndIndex} de {sortedVentasFiltradas.length}
            </div>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
              <select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value) || DEFAULT_PAGE_SIZE)}
                className="w-full sm:w-auto rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-[10px] font-black uppercase tracking-widest text-slate-500 outline-none"
                title="Cantidad de comprobantes visibles por pagina"
              >
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <option key={size} value={size}>{size} por pagina</option>
                ))}
              </select>
              <div className="flex items-center justify-center gap-2">
                <button
                  type="button"
                  onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                  disabled={safeCurrentPage <= 1}
                  className="px-3 py-2 rounded-xl border border-slate-200 bg-white text-[10px] font-black uppercase tracking-widest text-slate-500 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Anterior
                </button>
                <span className="min-w-[74px] text-center text-[10px] font-black uppercase tracking-widest text-slate-500">
                  {safeCurrentPage}/{totalPages}
                </span>
                <button
                  type="button"
                  onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                  disabled={safeCurrentPage >= totalPages}
                  className="px-3 py-2 rounded-xl border border-slate-200 bg-white text-[10px] font-black uppercase tracking-widest text-slate-500 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Siguiente
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Modal Detalle de Comprobante */}
      {detailModal.open && detailModal.venta && (() => {
        const v = detailModal.venta;
        const isFacturada = v.invoice_status === 'facturado';
        const isAnulada = v.invoice_status === 'anulado_nc';
        const isProc = !!processingMap[v.id];
        const entityRow = resolverEmisorParaVentaSafe(v);
        const vEmisorRow = validarEmisorAntesDeFacturar(entityRow, v?.total);
        const canFacturar = !isProc && !isFacturada && !isAnulada && ['pendiente','error'].includes(v.invoice_status) && vEmisorRow.ok;
        const items = Array.isArray(v.sale_items) ? v.sale_items : [];
        const subtotal = items.reduce((acc, it) => acc + Number(it.unit_price || 0) * Number(it.quantity || 0), 0);
        const totalNum = Number(v.total || 0);
        const descuento = subtotal > totalNum ? subtotal - totalNum : 0;
        const close = () => setDetailModal({ open: false, venta: null, loading: false });

        // Detalle impositivo: los comprobantes C (monotributo) no discriminan IVA.
        const discriminaIva = !(resolveCbteLabel(v) || '').toUpperCase().includes('C');
        let ivaRows = [];
        let netoGravado = 0, ivaTotal = 0, opEx = 0;
        if (Array.isArray(v.invoice_iva) && v.invoice_iva.length > 0) {
          // Snapshot real enviado a AFIP (fuente de verdad).
          ivaRows = v.invoice_iva.map((a) => ({ pct: afipPercentFromIvaId(a.Id), base: Number(a.BaseImp || 0), iva: Number(a.Importe || 0) }));
          netoGravado = Number(v.invoice_imp_neto || 0);
          ivaTotal = Number(v.invoice_imp_iva || 0);
          opEx = Number(v.invoice_imp_opex || 0);
        } else if (discriminaIva && items.length > 0) {
          // Ventas previas al snapshot: lo calculamos al vuelo (prorrateado al total real).
          const bd = buildAfipIvaBreakdown(items, { pricesIncludeIva: true, saleTotal: totalNum });
          ivaRows = bd.iva.map((a) => ({ pct: afipPercentFromIvaId(a.Id), base: a.BaseImp, iva: a.Importe }));
          netoGravado = bd.totals.ImpNeto; ivaTotal = bd.totals.ImpIVA; opEx = bd.totals.ImpOpEx;
        }
        const factorIva = subtotal > 0 && totalNum > 0 ? totalNum / subtotal : 1; // prorrateo de descuento por línea
        const showIvaCol = discriminaIva;

        return (
          <div
            className="fixed inset-0 z-[9998] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-5"
            onClick={close}
          >
            <div
              className="bg-white w-full max-w-4xl max-h-[92vh] rounded-3xl overflow-hidden flex flex-col shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="bg-[#0B2A36] px-4 sm:px-5 py-3.5 flex items-center justify-between gap-3 text-white">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
                    <FileText size={18} className="text-[#3FF2AA]" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-[8px] font-black uppercase tracking-[0.22em] text-[#3FF2AA]">Detalle de comprobante</div>
                    <div className="text-sm sm:text-base font-black truncate uppercase">
                      {resolveCbteLabel(v)}{v.numero_factura ? ` · ${v.numero_factura}` : ''}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span
                    title={v.invoice_status === 'error' ? (saleErrorById?.[v.id] || 'Error sin detalle') : ''}
                    className={`px-2 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest border ${
                      isFacturada ? 'bg-emerald-500/15 text-emerald-300 border-emerald-400/30' :
                      isAnulada ? 'bg-rose-500/15 text-rose-300 border-rose-400/30' :
                      v.invoice_status === 'enviado' ? 'bg-blue-500/15 text-blue-300 border-blue-400/30' :
                      v.invoice_status === 'error' ? 'bg-rose-500/15 text-rose-300 border-rose-400/30' :
                      'bg-amber-500/15 text-amber-300 border-amber-400/30'
                    }`}
                  >
                    {isAnulada ? 'ANULADA' : v.invoice_status}
                  </span>
                  <button onClick={close} className="bg-white/10 p-2 rounded-xl hover:bg-white/20 transition-all"><X size={18} /></button>
                </div>
              </div>

              {/* Body */}
              <div className="flex-1 overflow-auto bg-slate-50 p-4 sm:p-5 space-y-4">
                {/* Resumen */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                  <div className="bg-white rounded-xl border border-slate-200 p-3">
                    <div className="text-[8px] font-black uppercase tracking-widest text-slate-400">Total</div>
                    <div className="mt-1 text-base font-black text-slate-900">$ {fmtMoney(v.total)}</div>
                  </div>
                  <div className="bg-white rounded-xl border border-slate-200 p-3">
                    <div className="text-[8px] font-black uppercase tracking-widest text-slate-400">Fecha venta</div>
                    <div className="mt-1 text-[11px] font-bold text-slate-700">{formatDate(getSaleDate(v))}</div>
                    <div className="text-[9px] font-bold text-slate-400">{formatTimeAR(v.created_at)}</div>
                  </div>
                  <div className="bg-white rounded-xl border border-slate-200 p-3">
                    <div className="text-[8px] font-black uppercase tracking-widest text-slate-400">Fecha facturación</div>
                    <div className="mt-1 text-[11px] font-bold text-slate-700">{isFacturada ? formatDate(getBillingDate(v)) : '—'}</div>
                  </div>
                  <div className="bg-white rounded-xl border border-slate-200 p-3">
                    <div className="text-[8px] font-black uppercase tracking-widest text-slate-400">Sucursal</div>
                    <div className="mt-1 text-[11px] font-bold text-slate-700 truncate">{v.sitios?.nombre || '—'}</div>
                  </div>
                </div>

                {/* Cliente + Emisor */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="bg-white rounded-2xl border border-slate-200 p-4">
                    <div className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-2">Cliente</div>
                    <div className="text-[13px] font-black text-slate-800 uppercase">{v.clients?.full_name || 'Consumidor Final'}{v.origen === VENTA_ADMINISTRATIVA_ORIGEN && <span className="ml-1.5 align-middle px-1.5 py-0.5 rounded-md bg-violet-50 text-violet-600 text-[8px] font-black tracking-widest" title="Venta administrativa: fuera del arqueo de caja">ADMIN</span>}</div>
                    {v.origen === VENTA_ADMINISTRATIVA_ORIGEN && (
                      <div className="mt-1 text-[10px] font-bold text-violet-600">
                        Venta administrativa · {v.payment_method || 'Cuenta corriente'} · no entra al arqueo{v.afecta_stock === false ? ' · sin descuento de stock' : ''}
                      </div>
                    )}
                    <div className="mt-2 space-y-1 text-[10px] text-slate-500">
                      {v.clients?.tax_condition && <div><span className="font-black text-slate-400 uppercase tracking-widest text-[8px]">Cond. fiscal:</span> <span className="font-bold text-slate-600">{v.clients.tax_condition}</span></div>}
                      {(v.clients?.doc_type || v.clients?.doc_number) && (
                        <div><span className="font-black text-slate-400 uppercase tracking-widest text-[8px]">Doc:</span> <span className="font-bold text-slate-600">{v.clients?.doc_type || ''} {v.clients?.doc_number || '-'}</span></div>
                      )}
                      {v.clients?.address && <div><span className="font-black text-slate-400 uppercase tracking-widest text-[8px]">Dirección:</span> <span className="font-bold text-slate-600">{v.clients.address}{v.clients.city ? `, ${v.clients.city}` : ''}{v.clients.state ? `, ${v.clients.state}` : ''}</span></div>}
                      {v.clients?.phone && <div><span className="font-black text-slate-400 uppercase tracking-widest text-[8px]">Tel:</span> <span className="font-bold text-slate-600">{v.clients.phone}</span></div>}
                      {v.clients?.email && <div><span className="font-black text-slate-400 uppercase tracking-widest text-[8px]">Email:</span> <span className="font-bold text-slate-600">{v.clients.email}</span></div>}
                    </div>
                  </div>

                  <div className="bg-white rounded-2xl border border-slate-200 p-4">
                    <div className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-2">Información fiscal</div>
                    <div className="grid grid-cols-2 gap-x-3 gap-y-2 text-[10px]">
                      <div>
                        <div className="text-[8px] font-black uppercase tracking-widest text-slate-400">Tipo</div>
                        <div className="mt-0.5 font-black text-slate-700 uppercase">{resolveCbteLabel(v)}</div>
                      </div>
                      <div>
                        <div className="text-[8px] font-black uppercase tracking-widest text-slate-400">Nro. Comp.</div>
                        <div className="mt-0.5 font-bold text-slate-600">{v.numero_factura || '—'}</div>
                      </div>
                      <div className="col-span-2">
                        <div className="text-[8px] font-black uppercase tracking-widest text-slate-400">CAE</div>
                        <div className="mt-0.5 font-bold text-slate-600 break-all">{v.cae || '—'}</div>
                      </div>
                      <div>
                        <div className="text-[8px] font-black uppercase tracking-widest text-slate-400">Vto. CAE</div>
                        <div className="mt-0.5 font-bold text-slate-600">{v.cae_vencimiento ? new Date(v.cae_vencimiento).toLocaleDateString('es-AR', { timeZone: 'UTC' }) : '—'}</div>
                      </div>
                      <div>
                        <div className="text-[8px] font-black uppercase tracking-widest text-slate-400">Método pago</div>
                        <div className="mt-0.5 font-bold text-slate-600 uppercase">{v.payment_method || '—'}</div>
                      </div>
                      <div className="col-span-2">
                        <div className="text-[8px] font-black uppercase tracking-widest text-slate-400">Emisor</div>
                        <div className="mt-0.5 font-bold text-slate-700 uppercase">{entityRow?.tax_name || '— Sin asignar —'}</div>
                        {entityRow?.cuit && <div className="text-[9px] font-bold text-slate-400">CUIT {entityRow.cuit}</div>}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Items */}
                <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                  <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-100 flex items-center justify-between">
                    <div className="text-[9px] font-black uppercase tracking-widest text-slate-400">Ítems</div>
                    <div className="text-[9px] font-bold text-slate-400">{items.length} producto{items.length === 1 ? '' : 's'}</div>
                  </div>
                  {detailModal.loading && items.length === 0 ? (
                    <div className="px-4 py-6 text-center text-[10px] font-black uppercase tracking-widest text-slate-300 flex items-center justify-center gap-2">
                      <RefreshCcw size={14} className="animate-spin" /> Cargando ítems...
                    </div>
                  ) : items.length === 0 ? (
                    <div className="px-4 py-6 text-center text-[10px] font-black uppercase tracking-widest text-slate-300">Sin ítems</div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-left">
                        <thead className="text-[8px] font-black uppercase tracking-widest text-slate-400 bg-slate-50">
                          <tr>
                            <th className="px-4 py-2">Producto</th>
                            <th className="px-3 py-2 text-center w-[55px]">Cant.</th>
                            <th className="px-3 py-2 text-right w-[100px]">P. Unit.</th>
                            {showIvaCol && <th className="px-3 py-2 text-center w-[55px]">IVA</th>}
                            {showIvaCol && <th className="px-3 py-2 text-right w-[95px]">Neto</th>}
                            {showIvaCol && <th className="px-3 py-2 text-right w-[85px]">IVA $</th>}
                            <th className="px-3 py-2 text-right w-[105px]">Subtotal</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50">
                          {items.map((it, idx) => {
                            const esComponente = it.combo_role === 'component';
                            const sub = Number(it.unit_price || 0) * Number(it.quantity || 0);
                            // Importe facturable de la línea (prorrateado al total real).
                            const subFiscal = esComponente ? 0 : sub * factorIva;
                            const rate = getItemIvaPercent(it, 21);
                            const netoLinea = rate ? subFiscal / (1 + rate / 100) : subFiscal;
                            const ivaLinea = subFiscal - netoLinea;
                            return (
                              <tr key={it.id || idx} className={esComponente ? 'text-slate-400' : ''}>
                                <td className="px-4 py-2.5 text-[11px] font-bold">
                                  {esComponente ? '↳ ' : ''}{getSaleItemDisplayName(it)}
                                </td>
                                <td className="px-3 py-2.5 text-center text-[11px] font-bold text-slate-600">{Number(it.quantity || 0)}</td>
                                <td className="px-3 py-2.5 text-right text-[11px] font-bold text-slate-600">$ {fmtMoney(it.unit_price)}</td>
                                {showIvaCol && <td className="px-3 py-2.5 text-center text-[10px] font-black text-slate-500">{esComponente ? '' : `${rate}%`}</td>}
                                {showIvaCol && <td className="px-3 py-2.5 text-right text-[10px] font-bold text-slate-500">{esComponente ? '' : `$ ${fmtMoney(netoLinea)}`}</td>}
                                {showIvaCol && <td className="px-3 py-2.5 text-right text-[10px] font-bold text-slate-500">{esComponente ? '' : `$ ${fmtMoney(ivaLinea)}`}</td>}
                                <td className="px-3 py-2.5 text-right text-[11px] font-black text-slate-800">$ {fmtMoney(sub)}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                        <tfoot className="bg-slate-50/60">
                          {descuento > 0 && (
                            <tr>
                              <td colSpan={showIvaCol ? 6 : 3} className="px-4 py-2 text-right text-[10px] font-black uppercase tracking-widest text-slate-400">Subtotal</td>
                              <td className="px-3 py-2 text-right text-[11px] font-bold text-slate-600">$ {fmtMoney(subtotal)}</td>
                            </tr>
                          )}
                          {descuento > 0 && (
                            <tr>
                              <td colSpan={showIvaCol ? 6 : 3} className="px-4 py-2 text-right text-[10px] font-black uppercase tracking-widest text-slate-400">Descuento</td>
                              <td className="px-3 py-2 text-right text-[11px] font-bold text-rose-600">- $ {fmtMoney(descuento)}</td>
                            </tr>
                          )}
                          {showIvaCol && ivaRows.length > 0 && (
                            <>
                              <tr>
                                <td colSpan={6} className="px-4 py-2 text-right text-[10px] font-black uppercase tracking-widest text-slate-400">Neto gravado</td>
                                <td className="px-3 py-2 text-right text-[11px] font-bold text-slate-600">$ {fmtMoney(netoGravado)}</td>
                              </tr>
                              {ivaRows.map((r, i) => (
                                <tr key={`iva-${i}`}>
                                  <td colSpan={6} className="px-4 py-2 text-right text-[10px] font-black uppercase tracking-widest text-slate-400">IVA {r.pct}%</td>
                                  <td className="px-3 py-2 text-right text-[11px] font-bold text-slate-600">$ {fmtMoney(r.iva)}</td>
                                </tr>
                              ))}
                              {opEx > 0 && (
                                <tr>
                                  <td colSpan={6} className="px-4 py-2 text-right text-[10px] font-black uppercase tracking-widest text-slate-400">Op. exentas</td>
                                  <td className="px-3 py-2 text-right text-[11px] font-bold text-slate-600">$ {fmtMoney(opEx)}</td>
                                </tr>
                              )}
                            </>
                          )}
                          <tr>
                            <td colSpan={showIvaCol ? 6 : 3} className="px-4 py-3 text-right text-[10px] font-black uppercase tracking-widest text-slate-500">Total</td>
                            <td className="px-3 py-3 text-right text-base font-black text-slate-900">$ {fmtMoney(v.total)}</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  )}
                </div>

                {/* Error detalle si aplica */}
                {v.invoice_status === 'error' && saleErrorById?.[v.id] && (
                  <div className="bg-rose-50 border border-rose-200 rounded-2xl p-4 flex items-start gap-3">
                    <AlertTriangle size={18} className="text-rose-500 shrink-0 mt-0.5" />
                    <div>
                      <div className="text-[9px] font-black uppercase tracking-widest text-rose-700 mb-1">Último error AFIP</div>
                      <div className="text-[11px] font-bold text-rose-700 break-all">{saleErrorById[v.id]}</div>
                    </div>
                  </div>
                )}
              </div>

              {/* Footer acciones */}
              <div className="border-t border-slate-200 bg-white px-4 sm:px-5 py-3 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-2">
                <button onClick={close} className="px-4 py-2 rounded-xl border border-slate-200 text-slate-600 text-[10px] font-black uppercase tracking-widest hover:bg-slate-50">Cerrar</button>
                <div className="flex flex-wrap gap-2 sm:justify-end">
                  {v.invoice_status === 'error' && !isFacturada && !isAnulada && (
                    <button
                      onClick={() => facturarVenta(v.id)}
                      disabled={!vEmisorRow.ok || isProc}
                      title={!vEmisorRow.ok ? vEmisorRow.reason : 'Reintentar facturación'}
                      className={`px-4 py-2 rounded-xl shadow border text-[10px] font-black uppercase tracking-widest flex items-center gap-2 ${
                        (!vEmisorRow.ok || isProc)
                          ? 'bg-slate-100 text-slate-300 border-slate-200 cursor-not-allowed'
                          : 'bg-rose-500 text-white border-rose-500 hover:opacity-90'
                      }`}
                    >
                      <RefreshCcw size={14} /> {isProc ? 'Reintentando…' : 'Reintentar'}
                    </button>
                  )}
                  {!isFacturada && !isAnulada && (
                    <button
                      onClick={() => facturarVenta(v.id)}
                      disabled={!canFacturar}
                      title={!vEmisorRow.ok ? vEmisorRow.reason : 'Facturar'}
                      className={`px-4 py-2 rounded-xl shadow border text-[10px] font-black uppercase tracking-widest flex items-center gap-2 ${
                        canFacturar
                          ? 'bg-[#0B2A36] text-white border-[#0B2A36] hover:opacity-90'
                          : 'bg-slate-100 text-slate-300 border-slate-200 cursor-not-allowed'
                      }`}
                    >
                      <CloudUpload size={14} /> {isProc ? 'Procesando…' : 'Facturar'}
                    </button>
                  )}
                  {(isFacturada || isAnulada) && (
                    <button
                      onClick={() => handleImprimirNueva(v)}
                      className="px-4 py-2 rounded-xl shadow border border-[#0B2A36] bg-[#0B2A36] text-white text-[10px] font-black uppercase tracking-widest flex items-center gap-2 hover:opacity-90"
                    >
                      <Printer size={14} /> Imprimir
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Modal Reimpresión */}
      {printModal.open && (
        <div className="fixed inset-0 z-[9999] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-4xl h-[90vh] sm:h-[85vh] rounded-3xl overflow-hidden flex flex-col shadow-2xl">
            <div className="bg-[#0B2A36] p-4 sm:p-5 flex flex-col gap-3 text-white sm:flex-row sm:items-center sm:justify-between">
              <div className="flex min-w-0 items-center gap-3">
                <FileText size={20} className="text-[#3FF2AA]"/>
                <span className="font-black text-[10px] uppercase tracking-[0.2em]">Vista previa de impresión</span>
              </div>
              <div className="flex flex-col gap-2 sm:flex-row">
                <button onClick={() => { iframeRef.current?.contentWindow?.print(); }} className="w-full bg-[#3FF2AA] text-[#0B2A36] px-6 py-2.5 rounded-xl font-black text-[10px] uppercase tracking-widest shadow-lg sm:w-auto">Imprimir ahora</button>
                <button onClick={()=>setPrintModal({open:false, html:''})} className="bg-white/10 p-2.5 rounded-xl hover:bg-white/20 transition-all"><X size={20}/></button>
              </div>
            </div>
            <iframe ref={iframeRef} srcDoc={printModal.html} className="flex-1 w-full bg-slate-100 border-none"/>
          </div>
        </div>
      )}

      <div className="mt-3 text-[9px] font-bold text-slate-400 text-center">{BRAND_NAME} ERP • Facturación • Manual/Auto • Multi-Cbte</div>
      <style>{`
        .animate-nova-in { animation: novaFadeIn 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        @keyframes novaFadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </div>
  );
}





